/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  Users, Shield, Activity, Server, Database, AlertCircle, 
  CheckCircle, Clock, TrendingUp, DollarSign, Instagram, 
  HardDrive, RefreshCw, Settings, FileText, Terminal,
  ChevronRight, ChevronLeft, MoreVertical, Power, StopCircle, Play,
  Trash2, Eye, Download, Upload, Zap, Cpu, Box, Gift,
  ChevronUp, ChevronDown, Filter, X, Globe, Link2, UserX,
  Briefcase, CreditCard, ExternalLink, MousePointer, UserPlus,
  Smartphone, Monitor, Tablet, Chrome, Mail, Send, Code, UserCheck,
  Edit3, Image, PlusCircle, Search
} from 'lucide-react';
import InfluencerOutreach from '../components/InfluencerOutreach';
import BlogAdminPanel from '../components/BlogAdminPanel';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
/* eslint-enable no-unused-vars */

// Custom styles for email template editor to match email preview
const emailEditorStyles = `
  .email-editor .ql-editor {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 16px;
    line-height: 1.6;
    color: #4b5563;
    padding: 20px;
    background: #ffffff;
    min-height: 300px;
  }
  .email-editor .ql-editor h1 {
    color: #1f2937;
    font-size: 26px;
    font-weight: 700;
    margin-bottom: 16px;
  }
  .email-editor .ql-editor h2 {
    color: #1f2937;
    font-size: 22px;
    font-weight: 600;
    margin-bottom: 14px;
  }
  .email-editor .ql-editor h3 {
    color: #1f2937;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
  }
  .email-editor .ql-editor p {
    margin-bottom: 16px;
    color: #4b5563;
  }
  .email-editor .ql-editor a {
    color: #8b5cf6;
    text-decoration: none;
  }
  .email-editor .ql-editor ul, .email-editor .ql-editor ol {
    margin-bottom: 16px;
    padding-left: 24px;
  }
  .email-editor .ql-editor li {
    margin-bottom: 8px;
    color: #4b5563;
  }
  .email-editor .ql-editor strong {
    color: #1f2937;
    font-weight: 600;
  }
  .email-editor .ql-container {
    border: none !important;
    font-size: 16px;
  }
  .email-editor .ql-toolbar {
    border: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    background: #f9fafb;
  }
`;

const API_URL = process.env.REACT_APP_API_URL || 'https://vaulto.social/api';

// Professional color palette
const colors = {
  primary: '#6366F1',
  secondary: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  dark: '#1F2937',
  light: '#F9FAFB',
  muted: '#6B7280'
};

// Chart colors
const CHART_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'];

// Format large numbers (1500 -> "1.5K", 1000000 -> "1M")
const formatFollowerCount = (count) => {
  if (!count && count !== 0) return null;
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return count.toString();
};

// Styled components
const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = colors.primary }) => (
  <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 border border-gray-100">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center mb-2">
          <div className={`p-3 rounded-lg bg-gradient-to-br`} style={{ 
            background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)` 
          }}>
            <Icon size={24} style={{ color }} />
          </div>
          <h3 className="text-sm font-medium text-gray-600 ml-3">{title}</h3>
        </div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && (
          <div className="flex items-center mt-2">
            <p className="text-sm text-gray-500">{subtitle}</p>
            {trend && (
              <span className={`ml-2 text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

const ServiceStatusCard = ({ service, status, details, onAction }) => {
  const isActive = status === 'active';
  const statusColor = isActive ? colors.success : colors.danger;

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-100 hover:shadow-sm transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
          <div>
            <h4 className="font-medium text-gray-900 capitalize">{service}</h4>
            <p className="text-sm text-gray-500">{details || 'No details available'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onAction(service, 'restart')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Restart service"
          >
            <RefreshCw size={16} className="text-gray-600" />
          </button>
          {isActive ? (
            <button
              onClick={() => onAction(service, 'stop')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Stop service"
            >
              <StopCircle size={16} className="text-red-600" />
            </button>
          ) : (
            <button
              onClick={() => onAction(service, 'start')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Start service"
            >
              <Play size={16} className="text-green-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  
  // Tab navigation scroll state
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dashboard data
  const [overview, setOverview] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemResources, setSystemResources] = useState(null);
  const [servicesStatus, setServicesStatus] = useState(null);
  const [celeryQueues, setCeleryQueues] = useState(null);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState({});
  const [referralStats, setReferralStats] = useState(null);
  const [topReferrers, setTopReferrers] = useState([]);
  
  // Affiliate state
  const [affiliateDashboard, setAffiliateDashboard] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateConversions, setAffiliateConversions] = useState([]);
  const [affiliatePayouts, setAffiliatePayouts] = useState([]);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [affiliateSettings, setAffiliateSettings] = useState({ 
    signup_commission: '', 
    upgrade_commission: '',
    signup_payout_type: 'fixed',
    upgrade_payout_type: 'fixed',
    signup_revshare_percent: '',
    upgrade_revshare_percent: '',
    monthly_revshare_percent: '',
    annual_revshare_percent: '',
    revshare_months: ''
  });
  const [newPayout, setNewPayout] = useState({ amount: '', payment_method: '', transaction_reference: '', notes: '' });
  
  // Affiliate Applications state
  const [affiliateApplications, setAffiliateApplications] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [approvalSettings, setApprovalSettings] = useState({ 
    signup_commission: '1.00', 
    upgrade_commission: '5.00',
    signup_payout_type: 'fixed',
    upgrade_payout_type: 'fixed',
    signup_revshare_percent: '',
    upgrade_revshare_percent: '',
    monthly_revshare_percent: '',
    annual_revshare_percent: '',
    revshare_months: '',
    notes: '' 
  });
  const [rejectionReason, setRejectionReason] = useState('');
  
  // OAuth Logs state
  const [oauthLogs, setOauthLogs] = useState([]);
  const [oauthLogsStats, setOauthLogsStats] = useState(null);
  const [oauthLogsTotal, setOauthLogsTotal] = useState(0);
  const [oauthLogsFilters, setOauthLogsFilters] = useState({
    provider: '',
    event_type: '',
    error_type: ''
  });
  const [oauthLogsPage, setOauthLogsPage] = useState(1);
  const [oauthLogsPerPage, setOauthLogsPerPage] = useState(100);
  
  // Instagram Survey state
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [surveySummary, setSurveySummary] = useState({});
  const [surveyLoading, setSurveyLoading] = useState(false);
  
  // Blog SEO state
  const [blogStats, setBlogStats] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogPostsTotal, setBlogPostsTotal] = useState(0);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogPage, setBlogPage] = useState(1);
  const [blogPerPage] = useState(15);
  const [blogSearch, setBlogSearch] = useState('');
  const [blogCategoryFilter, setBlogCategoryFilter] = useState('');
  const [blogCategories, setBlogCategories] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateOptions, setGenerateOptions] = useState({ count: 5, category: '', dry_run: false });
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generationTaskId, setGenerationTaskId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  
  // Email Logs state
  const [emailStats, setEmailStats] = useState(null);
  const [allEmails, setAllEmails] = useState([]);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [emailFilters, setEmailFilters] = useState({
    email_type: '',
    status: '',
    search: ''
  });
  const [showEmailsModal, setShowEmailsModal] = useState(false);
  const [selectedUserForEmails, setSelectedUserForEmails] = useState(null);
  const [userEmails, setUserEmails] = useState([]);
  const [userEmailsLoading, setUserEmailsLoading] = useState(false);
  
  // Email Automation state
  const [emailFlows, setEmailFlows] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [automationUsers, setAutomationUsers] = useState([]);
  const [automationSubTab, setAutomationSubTab] = useState('flows');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateFlowFilter, setTemplateFlowFilter] = useState('');
  const [automationUserFilter, setAutomationUserFilter] = useState({ flow_id: '', status: '' });
  const [automationUsersPage, setAutomationUsersPage] = useState(0);
  const [automationUsersTotal, setAutomationUsersTotal] = useState(0);
  const [automationUsersLimit] = useState(25);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templatePreviewSending, setTemplatePreviewSending] = useState(false);
  const [triggerCheckLoading, setTriggerCheckLoading] = useState(false);
  const [triggerSendLoading, setTriggerSendLoading] = useState(false);
  const [templateEditorMode, setTemplateEditorMode] = useState('visual'); // 'visual' or 'code'

  // Quill editor modules configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  }), []);

  // Campaign state
  const [campaignStatus, setCampaignStatus] = useState(null);
  const [campaignUsers, setCampaignUsers] = useState([]);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignName] = useState('3month_trial_dec2024');
  const [campaignUsersPage, setCampaignUsersPage] = useState(1);
  const [campaignUsersTotal, setCampaignUsersTotal] = useState(0);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [selectedEligibleUsers, setSelectedEligibleUsers] = useState(new Set());
  const [sendingToSelected, setSendingToSelected] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  
  // Filtering and sorting states for users table
  const [userFilters, setUserFilters] = useState({
    email: '',
    role: '',
    subscription: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    referrer_domain: '',
    acquisition_source: '',
    signup_date_from: '',
    signup_date_to: '',
    country: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Pagination state for users table
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Get auth token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [
        overviewRes,
        healthRes,
        resourcesRes,
        servicesRes,
        alertsRes,
        referralStatsRes,
        topReferrersRes
      ] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard/overview`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/system/health`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/system/resources`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/services/status`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/alerts/recent`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/referrals/stats`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/referrals/top-referrers?limit=10`, getAuthHeaders())
      ]);

      setOverview(overviewRes.data);
      setSystemHealth(healthRes.data);
      setSystemResources(resourcesRes.data);
      setServicesStatus(servicesRes.data);
      setAlerts(alertsRes.data.alerts);
      setReferralStats(referralStatsRes.data);
      setTopReferrers(topReferrersRes.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      if (err.response?.status === 403) {
        navigate('/dashboard');
      }
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const exportUsersToCSV = () => {
    if (!users || users.length === 0) {
      toast.error('No users to export');
      return;
    }

    // Define CSV headers - all columns
    const headers = [
      'ID',
      'Sign Up Date',
      'Email',
      'Full Name',
      'Role',
      'Subscription Tier',
      'Is Active',
      'Google Drive Connected',
      'Instagram Accounts Count',
      'Instagram Usernames',
      'Instagram Followers',
      'Total Posts',
      'Backed Up Posts',
      'Backup Percentage',
      'UTM Source',
      'UTM Medium',
      'UTM Campaign',
      'UTM Term',
      'UTM Content',
      'Referrer Domain',
      'Acquisition Source',
      'Signup Method',
      'Referred By',
      'Affiliated By',
      'Country Code',
      'Country Name',
      'Device Type',
      'Device Brand',
      'Device Model',
      'Browser',
      'Browser Version',
      'Operating System',
      'OS Version',
      'Last Seen',
      'Sessions'
    ];

    // Convert users to CSV rows
    const rows = users.map(user => {
      const createdDate = user.created_at ? new Date(user.created_at).toLocaleString() : '';
      const lastSeenDate = user.last_seen_at ? new Date(user.last_seen_at).toLocaleString() : '';
      const instagramUsernames = user.instagram_accounts
        ? user.instagram_accounts.map(acc => `@${acc.username || acc.display_name || 'Unknown'}`).join('; ')
        : '';
      const instagramFollowers = user.instagram_accounts
        ? user.instagram_accounts.map(acc => acc.follower_count ? `@${acc.username || acc.display_name || 'Unknown'}: ${acc.follower_count.toLocaleString()}` : null).filter(Boolean).join('; ')
        : '';
      const totalFollowers = user.instagram_accounts
        ? user.instagram_accounts.reduce((sum, acc) => sum + (acc.follower_count || 0), 0)
        : 0;
      const backupPercentage = user.total_posts > 0
        ? Math.round((user.backed_up_posts / user.total_posts) * 100) + '%'
        : 'N/A';

      return [
        user.id || '',
        createdDate,
        user.email || '',
        user.full_name || '',
        user.is_admin ? 'Admin' : 'User',
        user.subscription_tier || 'Free',
        user.is_active ? 'Yes' : 'No',
        user.google_drive_connected ? 'Yes' : 'No',
        user.instagram_accounts?.length || 0,
        instagramUsernames,
        totalFollowers > 0 ? `${totalFollowers.toLocaleString()} (${instagramFollowers})` : '',
        user.total_posts || 0,
        user.backed_up_posts || 0,
        backupPercentage,
        user.utm_source || '',
        user.utm_medium || '',
        user.utm_campaign || '',
        user.utm_term || '',
        user.utm_content || '',
        user.referrer_domain || '',
        user.acquisition_source || 'organic',
        user.signup_method === 'oauth' ? 'Google OAuth' : (user.signup_method || 'Email/Password'),
        user.referred_by_email || '',
        user.affiliated_by_email || '',
        user.country || '',
        user.country_name || '',
        user.device_type || '',
        user.device_brand || '',
        user.device_model || '',
        user.browser || '',
        user.browser_version || '',
        user.operating_system || '',
        user.os_version || '',
        lastSeenDate,
        user.session_count || 0
      ];
    });

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Combine headers and rows
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `vaulto_users_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${users.length} users to CSV`);
  };

  // Fetch Celery queues
  const fetchCeleryQueues = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/celery/queues`, getAuthHeaders());
      setCeleryQueues(response.data);
    } catch (err) {
      console.error('Error fetching Celery queues:', err);
    }
  };

  // Fetch logs
  const fetchLogs = async (service) => {
    try {
      const response = await axios.get(`${API_URL}/admin/logs/${service}`, getAuthHeaders());
      setLogs(prev => ({ ...prev, [service]: response.data.logs }));
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };

  // Fetch OAuth connection logs
  const fetchOAuthLogs = async (page = oauthLogsPage, perPage = oauthLogsPerPage) => {
    try {
      const params = new URLSearchParams();
      if (oauthLogsFilters.provider) params.append('provider', oauthLogsFilters.provider);
      if (oauthLogsFilters.event_type) params.append('event_type', oauthLogsFilters.event_type);
      if (oauthLogsFilters.error_type) params.append('error_type', oauthLogsFilters.error_type);
      params.append('offset', ((page - 1) * perPage).toString());
      params.append('limit', perPage.toString());
      
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/oauth-logs?${params.toString()}`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/oauth-logs/stats`, getAuthHeaders())
      ]);
      
      setOauthLogs(logsRes.data.logs);
      setOauthLogsTotal(logsRes.data.total);
      setOauthLogsStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching OAuth logs:', err);
    }
  };

  // Export OAuth logs to CSV
  const exportOAuthLogsToCSV = async () => {
    try {
      // Fetch all logs with current filters (no pagination limit)
      const params = new URLSearchParams();
      if (oauthLogsFilters.provider) params.append('provider', oauthLogsFilters.provider);
      if (oauthLogsFilters.event_type) params.append('event_type', oauthLogsFilters.event_type);
      if (oauthLogsFilters.error_type) params.append('error_type', oauthLogsFilters.error_type);
      params.append('limit', '10000'); // High limit to get all logs
      
      toast.info('Fetching logs for export...');
      
      const response = await axios.get(`${API_URL}/admin/oauth-logs?${params.toString()}`, getAuthHeaders());
      const logs = response.data.logs;
      
      if (!logs || logs.length === 0) {
        toast.error('No logs to export');
        return;
      }

      // Define CSV headers
      const headers = [
        'ID',
        'Created At',
        'User ID',
        'User Email',
        'Account Username',
        'Provider',
        'Event Type',
        'Actual Account Type',
        'Selected Account Type',
        'Error Type',
        'Error Message',
        'Device Type',
        'Device Brand',
        'Device Model',
        'Browser',
        'Browser Version',
        'Operating System',
        'OS Version',
        'IP Address',
        'User Agent',
        'Metadata'
      ];

      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Generate CSV rows
      const rows = logs.map(log => [
        escapeCSV(log.id),
        escapeCSV(log.created_at ? new Date(log.created_at).toISOString() : ''),
        escapeCSV(log.user_id),
        escapeCSV(log.user_email),
        escapeCSV(log.account_username),
        escapeCSV(log.provider),
        escapeCSV(log.event_type),
        escapeCSV(log.actual_account_type),
        escapeCSV(log.selected_account_type),
        escapeCSV(log.error_type),
        escapeCSV(log.error_message),
        escapeCSV(log.device_type),
        escapeCSV(log.device_brand),
        escapeCSV(log.device_model),
        escapeCSV(log.browser),
        escapeCSV(log.browser_version),
        escapeCSV(log.operating_system),
        escapeCSV(log.os_version),
        escapeCSV(log.ip_address),
        escapeCSV(log.user_agent),
        escapeCSV(log.metadata ? JSON.stringify(log.metadata) : '')
      ].join(','));

      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with filters
      const filterParts = [];
      if (oauthLogsFilters.provider) filterParts.push(oauthLogsFilters.provider);
      if (oauthLogsFilters.event_type) filterParts.push(oauthLogsFilters.event_type);
      if (oauthLogsFilters.error_type) filterParts.push(oauthLogsFilters.error_type);
      const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
      
      link.setAttribute('download', `oauth_logs${filterSuffix}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${logs.length} logs to CSV`);
    } catch (err) {
      console.error('Error exporting OAuth logs:', err);
      toast.error('Failed to export logs');
    }
  };

  // Fetch Instagram survey responses
  const fetchSurveyResponses = async () => {
    setSurveyLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/instagram-survey-responses`, getAuthHeaders());
      setSurveyResponses(response.data.responses);
      setSurveySummary(response.data.reason_summary);
    } catch (err) {
      console.error('Error fetching survey responses:', err);
    } finally {
      setSurveyLoading(false);
    }
  };

  // Blog SEO functions
  const fetchBlogStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/blog/stats`, getAuthHeaders());
      setBlogStats(response.data);
    } catch (err) {
      console.error('Error fetching blog stats:', err);
    }
  };

  const fetchBlogPosts = async () => {
    setBlogLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', blogPage);
      params.append('per_page', blogPerPage);
      if (blogSearch) params.append('search', blogSearch);
      if (blogCategoryFilter) params.append('category', blogCategoryFilter);
      
      const response = await axios.get(`${API_URL}/admin/blog/posts?${params.toString()}`, getAuthHeaders());
      setBlogPosts(response.data.posts);
      setBlogPostsTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
    } finally {
      setBlogLoading(false);
    }
  };

  const fetchBlogCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/blog/categories`, getAuthHeaders());
      setBlogCategories(response.data.categories);
    } catch (err) {
      console.error('Error fetching blog categories:', err);
    }
  };

  const deleteBlogPost = async (slug) => {
    if (!window.confirm(`Are you sure you want to delete the post "${slug}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/admin/blog/posts/${slug}`, getAuthHeaders());
      toast.success('Blog post deleted successfully');
      fetchBlogStats();
      fetchBlogPosts();
    } catch (err) {
      console.error('Error deleting blog post:', err);
      toast.error('Failed to delete blog post');
    }
  };

  const generateBlogPosts = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8f685eea-05ab-4a04-b204-7990f7bb344e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AdminDashboard.js:generateBlogPosts:entry',message:'Generate button clicked',data:{generateOptions},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
    // #endregion
    setGenerateLoading(true);
    try {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8f685eea-05ab-4a04-b204-7990f7bb344e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AdminDashboard.js:generateBlogPosts:before_api',message:'About to call API',data:{url:`${API_URL}/admin/blog/generate`,options:generateOptions},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      const response = await axios.post(`${API_URL}/admin/blog/generate`, generateOptions, getAuthHeaders());
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8f685eea-05ab-4a04-b204-7990f7bb344e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AdminDashboard.js:generateBlogPosts:api_success',message:'API call succeeded',data:{task_id:response.data.task_id,message:response.data.message},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      setGenerationTaskId(response.data.task_id);
      toast.success(response.data.message);
      setShowGenerateModal(false);
      // Start polling for status
      pollGenerationStatus(response.data.task_id);
    } catch (err) {
      console.error('Error starting blog generation:', err);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8f685eea-05ab-4a04-b204-7990f7bb344e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AdminDashboard.js:generateBlogPosts:api_error',message:'API call failed',data:{error:err.message,response:err.response?.data},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      toast.error('Failed to start blog generation');
    } finally {
      setGenerateLoading(false);
    }
  };

  const pollGenerationStatus = async (taskId) => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/blog/generate/${taskId}/status`, getAuthHeaders());
        setGenerationStatus(response.data);
        
        if (response.data.status === 'running') {
          setTimeout(checkStatus, 5000); // Poll every 5 seconds
        } else {
          // Generation complete, refresh data
          fetchBlogStats();
          fetchBlogPosts();
          if (response.data.status === 'completed') {
            toast.success('Blog generation completed!');
          } else if (response.data.status === 'failed') {
            toast.error('Blog generation failed. Check logs for details.');
          }
        }
      } catch (err) {
        console.error('Error checking generation status:', err);
      }
    };
    checkStatus();
  };

  const regenerateImages = async (slug) => {
    try {
      const response = await axios.post(`${API_URL}/admin/blog/regenerate-images/${slug}`, {}, getAuthHeaders());
      toast.success(response.data.message);
    } catch (err) {
      console.error('Error regenerating images:', err);
      toast.error('Failed to regenerate images');
    }
  };

  // Fetch email stats for Emails tab
  const fetchEmailStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/emails/stats`, getAuthHeaders());
      setEmailStats(response.data);
    } catch (err) {
      console.error('Error fetching email stats:', err);
    }
  };

  // Fetch all emails for Emails tab
  const fetchAllEmails = async () => {
    try {
      const params = new URLSearchParams();
      if (emailFilters.email_type) params.append('email_type', emailFilters.email_type);
      if (emailFilters.status) params.append('status', emailFilters.status);
      if (emailFilters.search) params.append('search', emailFilters.search);
      
      const response = await axios.get(`${API_URL}/admin/emails?${params.toString()}`, getAuthHeaders());
      setAllEmails(response.data.emails);
      setEmailsTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching emails:', err);
    }
  };

  // Fetch emails for a specific user (modal)
  const fetchUserEmails = async (user) => {
    setSelectedUserForEmails(user);
    setUserEmailsLoading(true);
    setShowEmailsModal(true);
    try {
      const response = await axios.get(`${API_URL}/admin/users/${user.id}/emails`, getAuthHeaders());
      setUserEmails(response.data.emails);
    } catch (err) {
      console.error('Error fetching user emails:', err);
      toast.error('Failed to fetch emails for this user');
    } finally {
      setUserEmailsLoading(false);
    }
  };

  // Handle OAuth logs filter change
  const handleOauthFilterChange = (key, value) => {
    setOauthLogsFilters(prev => ({ ...prev, [key]: value }));
    setOauthLogsPage(1); // Reset to first page when filters change
  };

  // Email Automation Functions
  const fetchEmailFlows = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/email-flows`, getAuthHeaders());
      setEmailFlows(response.data.flows || []);
    } catch (err) {
      console.error('Error fetching email flows:', err);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const params = new URLSearchParams();
      if (templateFlowFilter) params.append('flow_id', templateFlowFilter);
      const response = await axios.get(`${API_URL}/admin/email-templates?${params.toString()}`, getAuthHeaders());
      setEmailTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching email templates:', err);
    }
  };

  const fetchAutomationUsers = async (page = automationUsersPage) => {
    try {
      const params = new URLSearchParams();
      if (automationUserFilter.flow_id) params.append('flow_id', automationUserFilter.flow_id);
      if (automationUserFilter.status) params.append('status_filter', automationUserFilter.status);
      params.append('limit', automationUsersLimit);
      params.append('offset', page * automationUsersLimit);
      const response = await axios.get(`${API_URL}/admin/email-automation/users?${params.toString()}`, getAuthHeaders());
      setAutomationUsers(response.data.users || []);
      setAutomationUsersTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching automation users:', err);
    }
  };

  const fetchTemplateDetails = async (templateId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/email-templates/${templateId}`, getAuthHeaders());
      setSelectedTemplate(response.data);
      setShowTemplateModal(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      toast.error('Failed to load template details');
    }
  };

  const updateEmailTemplate = async (templateId, updates) => {
    setTemplateSaving(true);
    try {
      await axios.put(`${API_URL}/admin/email-templates/${templateId}`, updates, getAuthHeaders());
      toast.success('Template updated successfully');
      setShowTemplateModal(false);
      fetchEmailTemplates();
      fetchEmailFlows();
    } catch (err) {
      console.error('Error updating template:', err);
      toast.error('Failed to update template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const sendTemplatePreview = async (templateId) => {
    setTemplatePreviewSending(true);
    try {
      await axios.post(`${API_URL}/admin/email-templates/${templateId}/preview`, {}, getAuthHeaders());
      toast.success('Preview email sent to your inbox!');
    } catch (err) {
      console.error('Error sending preview:', err);
      toast.error('Failed to send preview email');
    } finally {
      setTemplatePreviewSending(false);
    }
  };

  const toggleFlowActive = async (flowId, isActive) => {
    try {
      await axios.patch(`${API_URL}/admin/email-flows/${flowId}`, { is_active: !isActive }, getAuthHeaders());
      toast.success(`Flow ${isActive ? 'disabled' : 'enabled'}`);
      fetchEmailFlows();
    } catch (err) {
      console.error('Error toggling flow:', err);
      toast.error('Failed to update flow');
    }
  };

  const stopUserFromFlow = async (userId, flowId) => {
    try {
      await axios.post(`${API_URL}/admin/email-automation/users/${userId}/stop?flow_id=${flowId}&reason=manual`, {}, getAuthHeaders());
      toast.success('User stopped from flow');
      fetchAutomationUsers();
    } catch (err) {
      console.error('Error stopping user:', err);
      toast.error('Failed to stop user from flow');
    }
  };

  const triggerEmailCheck = async () => {
    setTriggerCheckLoading(true);
    try {
      const response = await axios.post(`${API_URL}/admin/email-automation/trigger-check`, {}, getAuthHeaders());
      toast.success(`Email trigger check started! Task ID: ${response.data.task_id?.slice(0, 8)}...`);
      // Refresh data after a delay to show results
      setTimeout(() => {
        fetchEmailFlows();
        fetchAutomationUsers();
      }, 3000);
    } catch (err) {
      console.error('Error triggering check:', err);
      toast.error(`Failed to trigger email check: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTriggerCheckLoading(false);
    }
  };

  const triggerEmailSend = async () => {
    setTriggerSendLoading(true);
    try {
      const response = await axios.post(`${API_URL}/admin/email-automation/trigger-send`, {}, getAuthHeaders());
      toast.success(`Email send task started! Task ID: ${response.data.task_id?.slice(0, 8)}...`);
      // Refresh data after a delay to show results
      setTimeout(() => {
        fetchAutomationUsers();
      }, 3000);
    } catch (err) {
      console.error('Error triggering send:', err);
      toast.error(`Failed to trigger email send: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTriggerSendLoading(false);
    }
  };

  // Service management
  const handleServiceAction = async (service, action) => {
    try {
      await axios.post(
        `${API_URL}/admin/services/${service}/${action}`,
        {},
        getAuthHeaders()
      );
      toast.success(`${service} ${action}ed successfully`);
      fetchDashboardData();
    } catch (err) {
      toast.error(`Failed to ${action} ${service}`);
    }
    setConfirmDialog({ open: false, action: null });
  };

  // Toggle admin status
  const handleToggleAdmin = async (userId, userEmail, isCurrentlyAdmin) => {
    const action = isCurrentlyAdmin ? 'remove admin privileges from' : 'grant admin privileges to';
    if (!window.confirm(`Are you sure you want to ${action} ${userEmail}?`)) {
      return;
    }

    try {
      await axios.put(`${API_URL}/admin/users/${userId}/toggle-admin`, {}, getAuthHeaders());
      toast.success(`Admin status updated for ${userEmail}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update admin status');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, getAuthHeaders());
      toast.success(`User ${userEmail} deleted successfully`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Set user plan
  const handleSetUserPlan = async (userId, userEmail, newTier) => {
    try {
      await axios.put(
        `${API_URL}/admin/users/${userId}/set-plan`, 
        { tier: newTier },
        getAuthHeaders()
      );
      toast.success(`Plan updated to ${newTier} for ${userEmail}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setUserFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear all filters
  const clearFilters = () => {
    setUserFilters({
      email: '',
      role: '',
      subscription: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      referrer_domain: '',
      acquisition_source: '',
      signup_date_from: '',
      signup_date_to: '',
      country: ''
    });
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = [...users];

    // Apply filters
    if (userFilters.email) {
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(userFilters.email.toLowerCase()) ||
        (user.full_name && user.full_name.toLowerCase().includes(userFilters.email.toLowerCase()))
      );
    }
    if (userFilters.role) {
      filtered = filtered.filter(user => 
        userFilters.role === 'admin' ? user.is_admin : !user.is_admin
      );
    }
    if (userFilters.subscription) {
      filtered = filtered.filter(user => 
        (user.subscription_tier || 'free').toLowerCase() === userFilters.subscription.toLowerCase()
      );
    }
    if (userFilters.utm_source) {
      filtered = filtered.filter(user => 
        user.utm_source && user.utm_source.toLowerCase().includes(userFilters.utm_source.toLowerCase())
      );
    }
    if (userFilters.utm_medium) {
      filtered = filtered.filter(user => 
        user.utm_medium && user.utm_medium.toLowerCase().includes(userFilters.utm_medium.toLowerCase())
      );
    }
    if (userFilters.utm_campaign) {
      filtered = filtered.filter(user => 
        user.utm_campaign && user.utm_campaign.toLowerCase().includes(userFilters.utm_campaign.toLowerCase())
      );
    }
    if (userFilters.utm_term) {
      filtered = filtered.filter(user => 
        user.utm_term && user.utm_term.toLowerCase().includes(userFilters.utm_term.toLowerCase())
      );
    }
    if (userFilters.utm_content) {
      filtered = filtered.filter(user => 
        user.utm_content && user.utm_content.toLowerCase().includes(userFilters.utm_content.toLowerCase())
      );
    }
    if (userFilters.referrer_domain) {
      filtered = filtered.filter(user => 
        user.referrer_domain && user.referrer_domain.toLowerCase().includes(userFilters.referrer_domain.toLowerCase())
      );
    }
    if (userFilters.acquisition_source) {
      filtered = filtered.filter(user => {
        const source = user.acquisition_source || 'organic';
        return source.toLowerCase() === userFilters.acquisition_source.toLowerCase();
      });
    }
    if (userFilters.signup_date_from) {
      const fromDate = new Date(userFilters.signup_date_from);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(user => {
        if (!user.created_at) return false;
        const userDate = new Date(user.created_at);
        return userDate >= fromDate;
      });
    }
    if (userFilters.signup_date_to) {
      const toDate = new Date(userFilters.signup_date_to);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(user => {
        if (!user.created_at) return false;
        const userDate = new Date(user.created_at);
        return userDate <= toDate;
      });
    }
    if (userFilters.country) {
      filtered = filtered.filter(user => 
        user.country && user.country.toLowerCase() === userFilters.country.toLowerCase()
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties
        if (sortConfig.key === 'role') {
          aValue = a.is_admin ? 'admin' : 'user';
          bValue = b.is_admin ? 'admin' : 'user';
        }

        // Handle date sorting for created_at
        if (sortConfig.key === 'created_at') {
          const aDate = aValue ? new Date(aValue).getTime() : 0;
          const bDate = bValue ? new Date(bValue).getTime() : 0;
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Convert to string for comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [users, userFilters, sortConfig]);

  // Calculate user statistics based on filtered users
  const userStats = useMemo(() => {
    const totalAll = users.length; // Total users (for percentage calculation)
    const filteredUsers = filteredAndSortedUsers;
    const total = filteredUsers.length;
    const onlyGoogleDrive = filteredUsers.filter(u => u.google_drive_connected && (!u.instagram_accounts || u.instagram_accounts.length === 0)).length;
    const onlyInstagram = filteredUsers.filter(u => !u.google_drive_connected && u.instagram_accounts && u.instagram_accounts.length > 0).length;
    const bothConnected = filteredUsers.filter(u => u.google_drive_connected && u.instagram_accounts && u.instagram_accounts.length > 0).length;
    const noConnections = filteredUsers.filter(u => !u.google_drive_connected && (!u.instagram_accounts || u.instagram_accounts.length === 0)).length;

    // Tier breakdown
    const tierCounts = filteredUsers.reduce((acc, user) => {
      const tier = (user.subscription_tier || 'free').toLowerCase();
      acc[tier] = (acc[tier] || 0) + 1;
      return acc;
    }, {});

    // Calculate percentages relative to filtered total
    const calcRate = (count) => total > 0 ? ((count / total) * 100).toFixed(1) : 0;

    return {
      totalAll,
      total,
      onlyGoogleDrive,
      onlyGoogleDriveRate: calcRate(onlyGoogleDrive),
      onlyInstagram,
      onlyInstagramRate: calcRate(onlyInstagram),
      bothConnected,
      bothConnectedRate: calcRate(bothConnected),
      noConnections,
      noConnectionsRate: calcRate(noConnections),
      tierCounts,
      isFiltered: total !== totalAll
    };
  }, [users, filteredAndSortedUsers]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [userFilters, sortConfig]);

  // Tab navigation scroll handling
  const updateScrollButtons = useCallback(() => {
    const container = tabsContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  }, []);

  const scrollTabs = useCallback((direction) => {
    const container = tabsContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (container && !loading) {
      // Initial check
      updateScrollButtons();
      // Also check after a brief delay to ensure DOM is fully rendered
      const timeoutId = setTimeout(updateScrollButtons, 100);
      
      container.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        clearTimeout(timeoutId);
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [updateScrollButtons, loading]);

  // Clear cache
  const handleClearCache = async () => {
    try {
      await axios.post(`${API_URL}/admin/maintenance/clear-cache`, {}, getAuthHeaders());
      toast.success('Cache cleared successfully');
    } catch (err) {
      toast.error('Failed to clear cache');
    }
  };

  // Purge Celery queue
  const handlePurgeQueue = async () => {
    try {
      await axios.post(`${API_URL}/admin/celery/purge`, {}, getAuthHeaders());
      toast.success('Queue purged successfully');
      fetchCeleryQueues();
    } catch (err) {
      toast.error('Failed to purge queue');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchUsers();
    fetchCeleryQueues();
  }, []);

  // Fetch OAuth logs when tab is active, filters change, or pagination changes
  useEffect(() => {
    if (activeTab === 'oauth') {
      fetchOAuthLogs(oauthLogsPage, oauthLogsPerPage);
    }
  }, [activeTab, oauthLogsFilters, oauthLogsPage, oauthLogsPerPage]);

  // Fetch affiliate data when tab is active
  useEffect(() => {
    if (activeTab === 'affiliates') {
      fetchAffiliateDashboard();
      fetchAffiliates();
      fetchAffiliateApplications();
    }
  }, [activeTab]);

  // Fetch survey data when tab is active
  useEffect(() => {
    if (activeTab === 'survey') {
      fetchSurveyResponses();
    }
  }, [activeTab]);

  // Fetch blog data when tab is active
  useEffect(() => {
    if (activeTab === 'blog') {
      fetchBlogStats();
      fetchBlogPosts();
      fetchBlogCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, blogPage, blogSearch, blogCategoryFilter]);

  // Fetch emails data when tab is active
  useEffect(() => {
    if (activeTab === 'emails') {
      fetchEmailStats();
      fetchAllEmails();
    }
  }, [activeTab, emailFilters]);

  // Fetch email automation data when tab is active
  useEffect(() => {
    if (activeTab === 'email-automation') {
      fetchEmailFlows();
      fetchEmailTemplates();
    }
  }, [activeTab, templateFlowFilter]);

  // Fetch automation users with pagination
  useEffect(() => {
    if (activeTab === 'email-automation') {
      fetchAutomationUsers(automationUsersPage);
    }
  }, [activeTab, automationUserFilter, automationUsersPage]);

  // Reset page when filter changes
  useEffect(() => {
    setAutomationUsersPage(0);
  }, [automationUserFilter]);

  // Fetch campaign data when tab is active
  useEffect(() => {
    if (activeTab === 'campaigns') {
      fetchCampaignStatus();
      fetchCampaignUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, campaignStatusFilter, campaignUsersPage]);

  // Campaign API functions
  const fetchCampaignStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/campaigns/status?campaign_name=${campaignName}`, getAuthHeaders());
      setCampaignStatus(response.data);
    } catch (err) {
      console.error('Error fetching campaign status:', err);
    }
  };

  const fetchCampaignUsers = async () => {
    try {
      const params = new URLSearchParams();
      params.append('campaign_name', campaignName);
      params.append('page', campaignUsersPage);
      params.append('per_page', 25);
      if (campaignStatusFilter) params.append('status_filter', campaignStatusFilter);
      
      const response = await axios.get(`${API_URL}/admin/campaigns/users?${params.toString()}`, getAuthHeaders());
      setCampaignUsers(response.data.users || []);
      setCampaignUsersTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching campaign users:', err);
    }
  };

  const fetchCampaignPreview = async () => {
    setCampaignLoading(true);
    setSelectedEligibleUsers(new Set()); // Reset selections when refreshing
    try {
      const response = await axios.get(`${API_URL}/admin/campaigns/preview?campaign_name=${campaignName}&limit=500`, getAuthHeaders());
      setCampaignPreview(response.data);
      toast.success(`Found ${response.data.total_eligible} eligible users`);
    } catch (err) {
      console.error('Error fetching campaign preview:', err);
      toast.error('Failed to preview eligible users');
    } finally {
      setCampaignLoading(false);
    }
  };

  const handleSelectEligibleUser = (userId) => {
    setSelectedEligibleUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAllEligible = () => {
    if (!campaignPreview?.users) return;
    
    const allUserIds = campaignPreview.users.map(u => u.id);
    if (selectedEligibleUsers.size === allUserIds.length) {
      // All selected, deselect all
      setSelectedEligibleUsers(new Set());
    } else {
      // Select all
      setSelectedEligibleUsers(new Set(allUserIds));
    }
  };

  const handleSendToSelected = async () => {
    if (selectedEligibleUsers.size === 0) {
      toast.error('No users selected');
      return;
    }

    setSendingToSelected(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/campaigns/send-to-selected?campaign_name=${campaignName}`,
        Array.from(selectedEligibleUsers),
        getAuthHeaders()
      );
      
      if (response.data.sent_count > 0) {
        toast.success(`Campaign emails queued for ${response.data.sent_count} users`);
        // Refresh preview to remove sent users
        fetchCampaignPreview();
        // Refresh campaign status
        fetchCampaignStatus();
        fetchCampaignUsers();
      } else {
        toast.warning(response.data.message);
      }
    } catch (err) {
      console.error('Error sending to selected:', err);
      toast.error(err.response?.data?.detail || 'Failed to send campaign emails');
    } finally {
      setSendingToSelected(false);
    }
  };

  const handleSendTestEmail = async () => {
    setCampaignLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('campaign_name', campaignName);
      if (testEmailAddress.trim()) {
        params.append('test_email', testEmailAddress.trim());
      }
      const response = await axios.post(`${API_URL}/admin/campaigns/send-test?${params.toString()}`, {}, getAuthHeaders());
      toast.success(response.data.message || 'Test email sent!');
    } catch (err) {
      console.error('Error sending test email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setCampaignLoading(false);
    }
  };

  const handleResendEmail = async (userId) => {
    try {
      await axios.post(`${API_URL}/admin/campaigns/resend/${userId}?campaign_name=${campaignName}`, {}, getAuthHeaders());
      toast.success('Email resent successfully');
    } catch (err) {
      console.error('Error resending email:', err);
      toast.error(err.response?.data?.detail || 'Failed to resend email');
    }
  };

  // Fetch affiliate dashboard
  const fetchAffiliateDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/affiliates/dashboard`, getAuthHeaders());
      setAffiliateDashboard(response.data);
    } catch (err) {
      console.error('Error fetching affiliate dashboard:', err);
    }
  };

  // Fetch all affiliates
  const fetchAffiliates = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/affiliates`, getAuthHeaders());
      setAffiliates(response.data.affiliates || []);
    } catch (err) {
      console.error('Error fetching affiliates:', err);
    }
  };

  // Fetch pending affiliate applications
  const fetchAffiliateApplications = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/affiliate-applications`, getAuthHeaders());
      setAffiliateApplications(response.data.applications || []);
    } catch (err) {
      console.error('Error fetching affiliate applications:', err);
    }
  };

  // Approve affiliate application
  const handleApproveApplication = async () => {
    if (!selectedApplication) return;
    try {
      const params = new URLSearchParams();
      
      // Payout types
      params.append('signup_payout_type', approvalSettings.signup_payout_type || 'fixed');
      params.append('upgrade_payout_type', approvalSettings.upgrade_payout_type || 'fixed');
      
      // Signup commission (fixed or revshare)
      if (approvalSettings.signup_payout_type === 'fixed') {
        params.append('signup_commission', approvalSettings.signup_commission || '1.00');
      } else {
        if (approvalSettings.signup_revshare_percent) {
          params.append('signup_revshare_percent', approvalSettings.signup_revshare_percent);
        }
      }
      
      // Upgrade commission (fixed or revshare)
      if (approvalSettings.upgrade_payout_type === 'fixed') {
        params.append('upgrade_commission', approvalSettings.upgrade_commission || '5.00');
      } else {
        if (approvalSettings.monthly_revshare_percent) {
          params.append('monthly_revshare_percent', approvalSettings.monthly_revshare_percent);
        }
        if (approvalSettings.annual_revshare_percent) {
          params.append('annual_revshare_percent', approvalSettings.annual_revshare_percent);
        }
        if (approvalSettings.revshare_months) {
          params.append('revshare_months', approvalSettings.revshare_months);
        }
      }
      
      if (approvalSettings.notes) params.append('notes', approvalSettings.notes);
      
      const response = await axios.post(
        `${API_URL}/admin/affiliate-applications/${selectedApplication.user_id}/approve?${params.toString()}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setShowApproveModal(false);
      setSelectedApplication(null);
      setApprovalSettings({ 
        signup_commission: '1.00', 
        upgrade_commission: '5.00',
        signup_payout_type: 'fixed',
        upgrade_payout_type: 'fixed',
        signup_revshare_percent: '',
        upgrade_revshare_percent: '',
        monthly_revshare_percent: '',
        annual_revshare_percent: '',
        revshare_months: '',
        notes: '' 
      });
      fetchAffiliateApplications();
      fetchAffiliates();
      fetchAffiliateDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve application');
    }
  };

  // Reject affiliate application
  const handleRejectApplication = async () => {
    if (!selectedApplication) return;
    try {
      const params = new URLSearchParams();
      if (rejectionReason) params.append('reason', rejectionReason);
      
      const response = await axios.post(
        `${API_URL}/admin/affiliate-applications/${selectedApplication.user_id}/reject?${params.toString()}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setShowRejectModal(false);
      setSelectedApplication(null);
      setRejectionReason('');
      fetchAffiliateApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject application');
    }
  };

  // Fetch affiliate conversions
  const fetchAffiliateConversions = async (affiliateId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/affiliates/${affiliateId}/conversions`, getAuthHeaders());
      setAffiliateConversions(response.data.conversions || []);
    } catch (err) {
      console.error('Error fetching affiliate conversions:', err);
    }
  };

  // Fetch affiliate payouts
  const fetchAffiliatePayouts = async (affiliateId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/affiliates/${affiliateId}/payouts`, getAuthHeaders());
      setAffiliatePayouts(response.data.payouts || []);
    } catch (err) {
      console.error('Error fetching affiliate payouts:', err);
    }
  };

  // Toggle affiliate status
  const handleToggleAffiliate = async (userId) => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/toggle-affiliate`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchUsers();
      fetchAffiliates();
    } catch (err) {
      toast.error('Failed to toggle affiliate status');
    }
  };

  // Update affiliate settings
  const handleUpdateAffiliateSettings = async () => {
    if (!selectedAffiliate) return;
    try {
      const params = new URLSearchParams();
      // Fixed payout amounts
      if (affiliateSettings.signup_commission) params.append('signup_commission', affiliateSettings.signup_commission);
      if (affiliateSettings.upgrade_commission) params.append('upgrade_commission', affiliateSettings.upgrade_commission);
      // Payout types
      if (affiliateSettings.signup_payout_type) params.append('signup_payout_type', affiliateSettings.signup_payout_type);
      if (affiliateSettings.upgrade_payout_type) params.append('upgrade_payout_type', affiliateSettings.upgrade_payout_type);
      // Revshare percentages
      if (affiliateSettings.signup_revshare_percent) params.append('signup_revshare_percent', affiliateSettings.signup_revshare_percent);
      if (affiliateSettings.upgrade_revshare_percent) params.append('upgrade_revshare_percent', affiliateSettings.upgrade_revshare_percent);
      // Monthly/Annual specific revshare percentages
      if (affiliateSettings.monthly_revshare_percent) params.append('monthly_revshare_percent', affiliateSettings.monthly_revshare_percent);
      if (affiliateSettings.annual_revshare_percent) params.append('annual_revshare_percent', affiliateSettings.annual_revshare_percent);
      // Revshare months
      if (affiliateSettings.revshare_months) params.append('revshare_months', affiliateSettings.revshare_months);
      
      const response = await axios.put(
        `${API_URL}/admin/users/${selectedAffiliate.id}/affiliate-settings?${params.toString()}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setShowAffiliateModal(false);
      fetchAffiliates();
    } catch (err) {
      toast.error('Failed to update affiliate settings');
    }
  };

  // Create payout
  const handleCreatePayout = async () => {
    if (!selectedAffiliate || !newPayout.amount) return;
    try {
      const params = new URLSearchParams();
      params.append('amount', newPayout.amount);
      if (newPayout.payment_method) params.append('payment_method', newPayout.payment_method);
      if (newPayout.transaction_reference) params.append('transaction_reference', newPayout.transaction_reference);
      if (newPayout.notes) params.append('notes', newPayout.notes);
      
      const response = await axios.post(
        `${API_URL}/admin/affiliates/${selectedAffiliate.id}/payouts?${params.toString()}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setShowPayoutModal(false);
      setNewPayout({ amount: '', payment_method: '', transaction_reference: '', notes: '' });
      fetchAffiliates();
      fetchAffiliatePayouts(selectedAffiliate.id);
    } catch (err) {
      toast.error('Failed to create payout');
    }
  };

  // Open affiliate detail modal
  const handleViewAffiliate = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setAffiliateSettings({
      signup_commission: affiliate.signup_commission || '',
      upgrade_commission: affiliate.upgrade_commission || '',
      signup_payout_type: affiliate.signup_payout_type || 'fixed',
      upgrade_payout_type: affiliate.upgrade_payout_type || 'fixed',
      signup_revshare_percent: affiliate.signup_revshare_percent || '',
      upgrade_revshare_percent: affiliate.upgrade_revshare_percent || '',
      monthly_revshare_percent: affiliate.monthly_revshare_percent || '',
      annual_revshare_percent: affiliate.annual_revshare_percent || '',
      revshare_months: affiliate.revshare_months || ''
    });
    fetchAffiliateConversions(affiliate.id);
    fetchAffiliatePayouts(affiliate.id);
    setShowAffiliateModal(true);
  };

  // Prepare chart data - Calculate user growth from actual user data
  const userGrowthData = React.useMemo(() => {
    if (!users || users.length === 0) return [];
    
    // Get the last 30 days of user registrations
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    // Create a map to store daily counts
    const dailyCounts = {};
    
    // Initialize all days with 0
    for (let i = 0; i < 30; i++) {
      const date = new Date(thirtyDaysAgo.getTime() + i * 24 * 60 * 60 * 1000);
      const dateKey = `${date.getMonth() + 1}/${date.getDate()}`;
      dailyCounts[dateKey] = 0;
    }
    
    // Count users by registration date
    users.forEach(user => {
      if (user.created_at) {
        const createdDate = new Date(user.created_at);
        if (createdDate >= thirtyDaysAgo) {
          const dateKey = `${createdDate.getMonth() + 1}/${createdDate.getDate()}`;
          if (dailyCounts.hasOwnProperty(dateKey)) {
            dailyCounts[dateKey]++;
          }
        }
      }
    });
    
    // Convert to array format for the chart
    return Object.entries(dailyCounts).map(([date, count]) => ({
      name: date,
      users: count
    }));
  }, [users]);

  const subscriptionData = [
    { name: 'Free', value: overview?.subscriptions.free || 0, color: colors.muted },
    { name: 'Premium', value: overview?.subscriptions.premium || 0, color: colors.primary },
    { name: 'Pro', value: overview?.subscriptions.pro || 0, color: colors.secondary },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-6 max-w-md">
          <AlertCircle className="text-red-500 mb-4" size={48} />
          <p className="text-gray-900 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'system', label: 'System', icon: Server },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'influencers', label: 'Influencer Outreach', icon: UserCheck },
    { id: 'campaigns', label: 'Campaigns', icon: Gift },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'email-automation', label: 'Email Automation', icon: Zap },
    { id: 'referrals', label: 'Referrals', icon: Gift },
    { id: 'affiliates', label: 'Affiliates', icon: Briefcase },
    { id: 'oauth', label: 'OAuth Logs', icon: Link2 },
    { id: 'survey', label: 'Survey', icon: FileText },
    { id: 'blog', label: 'Blog SEO', icon: Edit3 },
    { id: 'services', label: 'Services', icon: Settings },
    { id: 'logs', label: 'Logs', icon: Terminal },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="text-indigo-600 mr-3" size={28} />
              <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs - Scrollable with arrow buttons */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center">
            {/* Left scroll button - always render but control visibility */}
            <button
              onClick={() => scrollTabs('left')}
              className={`
                flex-shrink-0 p-2 mr-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 
                shadow-sm transition-all duration-200 hidden sm:flex items-center justify-center
                ${canScrollLeft ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'}
              `}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            
            {/* Scrollable tabs container */}
            <div 
              ref={tabsContainerRef}
              className="flex-1 overflow-x-auto scrollbar-thin scroll-smooth"
              style={{ scrollbarWidth: 'thin' }}
            >
              <nav className="flex space-x-1 sm:space-x-3 lg:space-x-6 min-w-max py-0.5">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center space-x-1 sm:space-x-2 py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors
                        ${activeTab === tab.id
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon size={16} className="flex-shrink-0" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
            
            {/* Right scroll button - always render but control visibility */}
            <button
              onClick={() => scrollTabs('right')}
              className={`
                flex-shrink-0 p-2 ml-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 
                shadow-sm transition-all duration-200 hidden sm:flex items-center justify-center
                ${canScrollRight ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'}
              `}
              disabled={!canScrollRight}
              aria-label="Scroll right"
            >
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={Users}
                title="Total Users"
                value={overview?.users.total || 0}
                subtitle={`${overview?.users.new_today || 0} new today`}
                trend={12}
                color={colors.primary}
              />
              <StatCard
                icon={DollarSign}
                title="Active Subscriptions"
                value={(overview?.subscriptions.premium || 0) + (overview?.subscriptions.pro || 0)}
                subtitle={`${overview?.subscriptions.premium || 0} Premium, ${overview?.subscriptions.pro || 0} Pro`}
                color={colors.success}
              />
              <StatCard
                icon={Instagram}
                title="Instagram Accounts"
                value={overview?.instagram.accounts || 0}
                subtitle={`${(overview?.instagram.posts || 0).toLocaleString()} posts`}
                color={colors.secondary}
              />
              <StatCard
                icon={Upload}
                title="Backups Today"
                value={overview?.backups.today || 0}
                subtitle={`${overview?.backups.failed_24h || 0} failed`}
                trend={-5}
                color={colors.info}
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Growth Chart */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={userGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="users" 
                      stroke={colors.primary} 
                      strokeWidth={3}
                      dot={{ fill: colors.primary, strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Subscription Distribution */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={subscriptionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {subscriptionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value, entry) => `${value}: ${entry.payload.value}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* System Health */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'Backend', status: systemHealth?.backend },
                  { name: 'Redis', status: systemHealth?.redis },
                  { name: 'Celery', status: systemHealth?.celery },
                  { name: 'Database', status: systemHealth?.database },
                ].map((service) => (
                  <div key={service.name} className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50">
                    {service.status ? (
                      <CheckCircle className="text-green-500" size={24} />
                    ) : (
                      <AlertCircle className="text-red-500" size={24} />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{service.name}</p>
                      <p className="text-sm text-gray-500">
                        {service.status ? 'Healthy' : 'Unhealthy'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Alerts */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Alerts</h3>
              {alerts.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="mx-auto text-green-500 mb-3" size={48} />
                  <p className="text-gray-500">No recent alerts - All systems operational</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${
                        alert.severity === 'error'
                          ? 'bg-red-50 border-red-200'
                          : alert.severity === 'warning'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start">
                        <AlertCircle
                          className={`mr-3 mt-0.5 ${
                            alert.severity === 'error'
                              ? 'text-red-600'
                              : alert.severity === 'warning'
                              ? 'text-yellow-600'
                              : 'text-blue-600'
                          }`}
                          size={20}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{alert.message}</p>
                          <p className="text-sm text-gray-500 mt-1">{alert.timestamp}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* System Tab */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* CPU Usage */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">CPU Usage</h3>
                  <Cpu className="text-gray-400" size={24} />
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Current Usage</span>
                      <span className="font-medium">{systemResources?.cpu?.percent?.toFixed(1) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${systemResources?.cpu?.percent || 0}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    Load Average: {systemResources?.cpu?.load_avg?.join(', ') || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Memory Usage */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Memory Usage</h3>
                  <Box className="text-gray-400" size={24} />
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Current Usage</span>
                      <span className="font-medium">{systemResources?.memory?.percent?.toFixed(1) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${systemResources?.memory?.percent || 0}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Used: {formatBytes(systemResources?.memory?.used)}</span>
                    <span>Total: {formatBytes(systemResources?.memory?.total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Disk Usage */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Disk Usage</h3>
                <HardDrive className="text-gray-400" size={24} />
              </div>
              <div className="space-y-4">
                {systemResources?.disk && Object.entries(systemResources.disk).map(([mount, info]) => (
                  <div key={mount}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 font-medium">{mount}</span>
                      <span className="font-medium">{info.percent?.toFixed(1) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          info.percent > 80 ? 'bg-red-500' : info.percent > 60 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${info.percent || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatBytes(info.used)} / {formatBytes(info.total)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleClearCache}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                >
                  <Trash2 size={16} />
                  <span>Clear Cache</span>
                </button>
                <button
                  onClick={() => fetchDashboardData()}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <RefreshCw size={16} />
                  <span>Refresh Data</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Referral Analytics</h2>
              
              {referralStats ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <StatCard
                      icon={Users}
                      title="Total Referrals"
                      value={referralStats.total_referrals}
                      color={colors.primary}
                    />
                    <StatCard
                      icon={CheckCircle}
                      title="Converted"
                      value={referralStats.total_converted}
                      color={colors.success}
                    />
                    <StatCard
                      icon={Gift}
                      title="Rewarded"
                      value={referralStats.total_rewarded}
                      color={colors.warning}
                    />
                    <StatCard
                      icon={TrendingUp}
                      title="Conversion Rate"
                      value={`${referralStats.conversion_rate}%`}
                      color={colors.secondary}
                    />
                  </div>

                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Referrers</h3>
                    {topReferrers && topReferrers.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rewarded</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {topReferrers.map((referrer) => (
                              <tr key={referrer.user_id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {referrer.full_name || referrer.email}
                                    </div>
                                    <div className="text-sm text-gray-500">{referrer.email}</div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                                  {referrer.referral_code}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {referrer.total_referrals}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {referrer.rewarded_referrals}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {referrer.conversion_rate}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500">No referrers yet</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* User Statistics Summary */}
            {userStats.isFiltered && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 flex items-center space-x-2">
                <Filter size={16} className="text-indigo-600" />
                <span className="text-sm text-indigo-700">
                  Showing stats for <strong>{userStats.total}</strong> filtered users out of <strong>{userStats.totalAll}</strong> total
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <div className="flex items-center space-x-2 mb-1">
                  <Users size={18} className="text-indigo-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">
                    {userStats.isFiltered ? 'Filtered Users' : 'Total Users'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{userStats.total}</p>
                {userStats.isFiltered && (
                  <p className="text-xs text-gray-500">of {userStats.totalAll} total</p>
                )}
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <div className="flex items-center space-x-2 mb-1">
                  <HardDrive size={18} className="text-blue-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">Drive Only</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{userStats.onlyGoogleDrive}</p>
                <p className="text-xs text-gray-500">{userStats.onlyGoogleDriveRate}%</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <div className="flex items-center space-x-2 mb-1">
                  <Instagram size={18} className="text-pink-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">Instagram Only</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{userStats.onlyInstagram}</p>
                <p className="text-xs text-gray-500">{userStats.onlyInstagramRate}%</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <div className="flex items-center space-x-2 mb-1">
                  <CheckCircle size={18} className="text-green-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">Both Connected</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{userStats.bothConnected}</p>
                <p className="text-xs text-gray-500">{userStats.bothConnectedRate}%</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <div className="flex items-center space-x-2 mb-1">
                  <AlertCircle size={18} className="text-gray-400" />
                  <span className="text-xs font-medium text-gray-500 uppercase">No Connections</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{userStats.noConnections}</p>
                <p className="text-xs text-gray-500">{userStats.noConnectionsRate}%</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-medium text-gray-500 uppercase mb-1">By Tier</span>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(userStats.tierCounts).map(([tier, count]) => {
                      const rate = userStats.total > 0 ? ((count / userStats.total) * 100).toFixed(1) : 0;
                      return (
                        <span 
                          key={tier}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            tier === 'pro' ? 'bg-indigo-100 text-indigo-800' :
                            tier === 'premium' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}
                          title={`${rate}%`}
                        >
                          {tier}: {count} ({rate}%)
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={exportUsersToCSV}
                    className="px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center space-x-1 sm:space-x-2 text-sm"
                    title="Export users to CSV"
                  >
                    <Download size={16} />
                    <span className="hidden sm:inline">Export CSV</span>
                  </button>
                  <button
                    onClick={fetchUsers}
                    className="px-3 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-1 sm:space-x-2 text-sm"
                  >
                    <RefreshCw size={16} />
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                </div>
              </div>
              
              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 mb-3 sm:mb-4">
                <input
                  type="text"
                  placeholder="Filter by email/name..."
                  value={userFilters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <select
                  value={userFilters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
                <select
                  value={userFilters.subscription}
                  onChange={(e) => handleFilterChange('subscription', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Subscriptions</option>
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                  <option value="pro">Pro</option>
                </select>
                <input
                  type="text"
                  placeholder="UTM Source..."
                  value={userFilters.utm_source}
                  onChange={(e) => handleFilterChange('utm_source', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="text"
                  placeholder="UTM Medium..."
                  value={userFilters.utm_medium}
                  onChange={(e) => handleFilterChange('utm_medium', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="UTM Campaign..."
                  value={userFilters.utm_campaign}
                  onChange={(e) => handleFilterChange('utm_campaign', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="text"
                  placeholder="UTM Term..."
                  value={userFilters.utm_term}
                  onChange={(e) => handleFilterChange('utm_term', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="text"
                  placeholder="UTM Content..."
                  value={userFilters.utm_content}
                  onChange={(e) => handleFilterChange('utm_content', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <input
                  type="text"
                  placeholder="Referrer Domain..."
                  value={userFilters.referrer_domain}
                  onChange={(e) => handleFilterChange('referrer_domain', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <select
                  value={userFilters.acquisition_source}
                  onChange={(e) => handleFilterChange('acquisition_source', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Sources</option>
                  <option value="organic">Organic</option>
                  <option value="non-organic">Non-Organic</option>
                </select>
                <input
                  type="text"
                  placeholder="Country Code (e.g., US)..."
                  value={userFilters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value.toUpperCase())}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                  maxLength={2}
                />
                <div className="flex items-center space-x-1">
                  <label className="text-xs text-gray-500 whitespace-nowrap">From:</label>
                  <input
                    type="date"
                    value={userFilters.signup_date_from}
                    onChange={(e) => handleFilterChange('signup_date_from', e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1"
                  />
                </div>
                <div className="flex items-center space-x-1">
                  <label className="text-xs text-gray-500 whitespace-nowrap">To:</label>
                  <input
                    type="date"
                    value={userFilters.signup_date_to}
                    onChange={(e) => handleFilterChange('signup_date_to', e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1"
                  />
                </div>
              </div>
              {(Object.values(userFilters).some(filter => filter !== '')) && (
                <button
                  onClick={clearFilters}
                  className="mt-3 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 flex items-center space-x-1"
                >
                  <X size={14} />
                  <span>Clear Filters</span>
                </button>
              )}
            </div>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {/* Sign Up - sticky first column */}
                    <th
                      className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap sticky left-0 bg-gray-50 z-20 min-w-[85px] sm:min-w-[95px] border-r border-gray-200"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center space-x-1">
                        <Clock size={14} className="text-gray-400" />
                        <span>Sign Up</span>
                        {sortConfig.key === 'created_at' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    {/* User - sticky second column */}
                    <th
                      className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap sticky left-[85px] sm:left-[95px] bg-gray-50 z-20 min-w-[150px] sm:min-w-[180px] border-r border-gray-200"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>User</span>
                        {sortConfig.key === 'email' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('role')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Role</span>
                        {sortConfig.key === 'role' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('subscription_tier')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Tier</span>
                        {sortConfig.key === 'subscription_tier' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('google_drive_connected')}
                    >
                      <div className="flex items-center space-x-1">
                        <HardDrive size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Drive</span>
                        {sortConfig.key === 'google_drive_connected' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('instagram_accounts_count')}
                    >
                      <div className="flex items-center space-x-1">
                        <Instagram size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Instagram</span>
                        {sortConfig.key === 'instagram_accounts_count' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('total_posts')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Posts</span>
                        {sortConfig.key === 'total_posts' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('backed_up_posts')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Backed Up</span>
                        {sortConfig.key === 'backed_up_posts' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('utm_source')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>UTM Src</span>
                        {sortConfig.key === 'utm_source' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('utm_medium')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>UTM Med</span>
                        {sortConfig.key === 'utm_medium' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('utm_campaign')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>UTM Camp</span>
                        {sortConfig.key === 'utm_campaign' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('utm_term')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>UTM Term</span>
                        {sortConfig.key === 'utm_term' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('utm_content')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>UTM Cont</span>
                        {sortConfig.key === 'utm_content' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('referrer_domain')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Referrer</span>
                        {sortConfig.key === 'referrer_domain' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('acquisition_source')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Acq Src</span>
                        {sortConfig.key === 'acquisition_source' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('signup_method')}
                    >
                      <div className="flex items-center space-x-1">
                        <UserPlus size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Method</span>
                        {sortConfig.key === 'signup_method' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('referred_by_email')}
                    >
                      <div className="flex items-center space-x-1">
                        <Gift size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Referred</span>
                        {sortConfig.key === 'referred_by_email' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('affiliated_by_email')}
                    >
                      <div className="flex items-center space-x-1">
                        <Briefcase size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Affiliate</span>
                        {sortConfig.key === 'affiliated_by_email' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('country')}
                    >
                      <div className="flex items-center space-x-1">
                        <Globe size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Country</span>
                        {sortConfig.key === 'country' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('device_type')}
                    >
                      <div className="flex items-center space-x-1">
                        <Smartphone size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Device</span>
                        {sortConfig.key === 'device_type' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('browser')}
                    >
                      <div className="flex items-center space-x-1">
                        <Chrome size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Browser</span>
                        {sortConfig.key === 'browser' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('operating_system')}
                    >
                      <div className="flex items-center space-x-1">
                        <Monitor size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">OS</span>
                        {sortConfig.key === 'operating_system' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('last_seen_at')}
                    >
                      <div className="flex items-center space-x-1">
                        <Clock size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Last Seen</span>
                        {sortConfig.key === 'last_seen_at' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('session_count')}
                    >
                      <div className="flex items-center space-x-1">
                        <Activity size={14} className="text-gray-400" />
                        <span className="hidden sm:inline">Sessions</span>
                        {sortConfig.key === 'session_count' && (
                          sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="25" className="px-6 py-8 text-center text-gray-500">
                        No users found matching the filters
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        {/* Sign Up - sticky first column */}
                        <td className="px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap sticky left-0 bg-white z-10 min-w-[85px] sm:min-w-[95px] border-r border-gray-100">
                          {user.created_at ? (
                            <div className="text-xs">
                              <div className="text-gray-900 font-medium">
                                {new Date(user.created_at).toLocaleDateString()}
                              </div>
                              <div className="text-gray-500">
                                {new Date(user.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        {/* User - sticky second column */}
                        <td className="px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap sticky left-[85px] sm:left-[95px] bg-white z-10 min-w-[150px] sm:min-w-[180px] border-r border-gray-100">
                          <div>
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[140px] sm:max-w-[170px]" title={user.email}>{user.email}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[140px] sm:max-w-[170px]">{user.full_name || 'No name'}</div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.is_admin 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.is_admin ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <select
                            value={(user.subscription_tier || 'free').toLowerCase()}
                            onChange={(e) => handleSetUserPlan(user.id, user.email, e.target.value)}
                            className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-indigo-500 ${
                              (user.subscription_tier || 'free').toLowerCase() === 'agency'
                                ? 'bg-purple-100 text-purple-800'
                                : (user.subscription_tier || 'free').toLowerCase() === 'pro'
                                ? 'bg-indigo-100 text-indigo-800'
                                : (user.subscription_tier || 'free').toLowerCase() === 'premium'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <option value="free">Free</option>
                            <option value="premium">Premium</option>
                            <option value="pro">Pro</option>
                            <option value="agency">Agency</option>
                          </select>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.google_drive_connected ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                              <span className="hidden sm:inline">Connected</span>
                              <span className="sm:hidden">✓</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              <AlertCircle size={12} className="mr-1 flex-shrink-0" />
                              <span className="hidden sm:inline">No</span>
                              <span className="sm:hidden">✗</span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.instagram_accounts && user.instagram_accounts.length > 0 ? (
                            <div className="space-y-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                                <Instagram size={12} className="mr-1 flex-shrink-0" />
                                {user.instagram_accounts.length}
                              </span>
                              <div className="text-xs text-gray-500 max-w-[150px]">
                                {user.instagram_accounts.slice(0, 2).map((acc) => (
                                  <div key={acc.id} className="flex items-center justify-between gap-1" title={`@${acc.username || acc.display_name || 'Unknown'}${acc.follower_count ? ` - ${acc.follower_count.toLocaleString()} followers` : ''}`}>
                                    <span className="truncate">@{acc.username || acc.display_name || 'Unknown'}</span>
                                    {acc.follower_count ? (
                                      <span className="text-purple-600 font-medium flex-shrink-0">{formatFollowerCount(acc.follower_count)}</span>
                                    ) : null}
                                  </div>
                                ))}
                                {user.instagram_accounts.length > 2 && (
                                  <span className="text-gray-400">+{user.instagram_accounts.length - 2} more</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{user.total_posts || 0}</span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.total_posts > 0 ? (
                            <div className="flex items-center space-x-1">
                              <span className="text-sm font-medium text-gray-900">{user.backed_up_posts || 0}</span>
                              <span className={`text-xs px-1 py-0.5 rounded ${
                                user.backed_up_posts === user.total_posts 
                                  ? 'bg-green-100 text-green-700' 
                                  : user.backed_up_posts > 0 
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-gray-100 text-gray-500'
                              }`}>
                                {Math.round((user.backed_up_posts / user.total_posts) * 100)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.utm_source ? (
                            <span className="text-xs text-gray-900 truncate max-w-[80px] block" title={user.utm_source}>{user.utm_source}</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.utm_medium ? (
                            <span className="text-xs text-gray-900 truncate max-w-[80px] block" title={user.utm_medium}>{user.utm_medium}</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.utm_campaign ? (
                            <span className="text-xs text-gray-900 truncate max-w-[80px] block" title={user.utm_campaign}>{user.utm_campaign}</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.utm_term ? (
                            <span className="text-xs text-gray-900 truncate max-w-[80px] block" title={user.utm_term}>{user.utm_term}</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.utm_content ? (
                            <span className="text-xs text-gray-900 truncate max-w-[80px] block" title={user.utm_content}>{user.utm_content}</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.referrer_domain ? (
                            <span className="text-xs text-gray-900 truncate max-w-[100px] block" title={user.referrer_domain}>
                              {user.referrer_domain.length > 20 ? `${user.referrer_domain.substring(0, 20)}...` : user.referrer_domain}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            (user.acquisition_source || 'organic') === 'non-organic'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {(user.acquisition_source || 'organic') === 'non-organic' ? 'Paid' : 'Org'}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.signup_method ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.signup_method === 'oauth'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.signup_method === 'oauth' ? 'OAuth' : 'Email'}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.referred_by_email ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 truncate max-w-[100px]" title={user.referred_by_email}>
                              <Gift size={12} className="mr-1 flex-shrink-0" />
                              <span className="truncate">{user.referred_by_email.split('@')[0]}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.affiliated_by_email ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 truncate max-w-[100px]" title={user.affiliated_by_email}>
                              <Briefcase size={12} className="mr-1 flex-shrink-0" />
                              <span className="truncate">{user.affiliated_by_email.split('@')[0]}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.country ? (
                            <span className="text-sm font-medium text-gray-900" title={user.country_name || user.country}>
                              {user.country}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.device_type ? (
                            <div className="flex items-center space-x-1">
                              {user.device_type === 'mobile' && <Smartphone size={14} className="text-blue-500" />}
                              {user.device_type === 'desktop' && <Monitor size={14} className="text-gray-500" />}
                              {user.device_type === 'tablet' && <Tablet size={14} className="text-purple-500" />}
                              <span className="text-xs text-gray-900 capitalize hidden sm:inline">{user.device_type}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.browser ? (
                            <span className="text-xs font-medium text-gray-900 truncate max-w-[60px] block" title={user.browser}>
                              {user.browser}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.operating_system ? (
                            <span className="text-xs font-medium text-gray-900 truncate max-w-[60px] block" title={user.operating_system}>
                              {user.operating_system}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {user.last_seen_at ? (
                            <div className="text-xs">
                              <div className="text-gray-900 font-medium">
                                {new Date(user.last_seen_at).toLocaleDateString()}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Never</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <div className="text-xs">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                              (user.session_count || 0) === 0 
                                ? 'bg-gray-100 text-gray-600' 
                                : (user.session_count || 0) >= 10 
                                  ? 'bg-green-100 text-green-800' 
                                  : (user.session_count || 0) >= 5 
                                    ? 'bg-blue-100 text-blue-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.session_count || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <button
                              onClick={() => navigate(`/admin/users/${user.id}`)}
                              className="text-indigo-600 hover:text-indigo-900 p-1"
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => handleToggleAdmin(user.id, user.email, user.is_admin)}
                              className={`p-1 ${user.is_admin ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                              title={user.is_admin ? 'Remove Admin' : 'Make Admin'}
                            >
                              <Shield size={16} />
                            </button>
                            <button
                              onClick={() => handleToggleAffiliate(user.id)}
                              className={`p-1 ${user.is_affiliate ? 'text-indigo-600 hover:text-indigo-900' : 'text-gray-400 hover:text-indigo-600'}`}
                              title={user.is_affiliate ? 'Remove Affiliate' : 'Make Affiliate'}
                            >
                              <Briefcase size={16} />
                            </button>
                            <button
                              onClick={() => fetchUserEmails(user)}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="View Sent Emails"
                            >
                              <Mail size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Delete User"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
              
              {/* Pagination Controls */}
              {filteredAndSortedUsers.length > 0 && (
                <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <span className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredAndSortedUsers.length)}</span> of{' '}
                      <span className="font-medium">{filteredAndSortedUsers.length}</span> users
                      {filteredAndSortedUsers.length !== users.length && (
                        <span className="text-gray-500"> (filtered from {users.length})</span>
                      )}
                    </span>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-600">Per page:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages || 1}</span>
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        )}

        {/* Influencer Outreach Tab */}
        {activeTab === 'influencers' && (
          <InfluencerOutreach />
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-6">
            {/* Campaign Controls */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">3-Month Trial Campaign</h2>
                  <p className="text-sm text-gray-500">
                    Send promotional emails offering 3 months free Premium to users who haven't completed setup
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={fetchCampaignPreview}
                    disabled={campaignLoading}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {campaignLoading ? 'Loading...' : 'Preview Eligible Users'}
                  </button>
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      placeholder="Email for test (optional)"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
                    />
                    <button
                      onClick={handleSendTestEmail}
                      disabled={campaignLoading}
                      className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      Send Test Email
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview Results */}
              {campaignPreview && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">
                      Eligible Users: {campaignPreview.total_eligible}
                      {selectedEligibleUsers.size > 0 && (
                        <span className="ml-2 text-blue-600">({selectedEligibleUsers.size} selected)</span>
                      )}
                    </h3>
                    {selectedEligibleUsers.size > 0 && (
                      <button
                        onClick={handleSendToSelected}
                        disabled={sendingToSelected}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {sendingToSelected ? 'Sending...' : `Send to ${selectedEligibleUsers.size} Selected`}
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">
                            <input
                              type="checkbox"
                              checked={campaignPreview.users?.length > 0 && selectedEligibleUsers.size === campaignPreview.users.length}
                              onChange={handleSelectAllEligible}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Sign Up</th>
                          <th className="px-3 py-2 text-left">Last Seen</th>
                          <th className="px-3 py-2 text-left">IG</th>
                          <th className="px-3 py-2 text-left">Drive</th>
                          <th className="px-3 py-2 text-left">Backup</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaignPreview.users?.map((user, idx) => (
                          <tr
                            key={idx}
                            className={`border-b border-gray-100 cursor-pointer hover:bg-gray-100 ${selectedEligibleUsers.has(user.id) ? 'bg-blue-50' : ''}`}
                            onClick={() => handleSelectEligibleUser(user.id)}
                          >
                            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedEligibleUsers.has(user.id)}
                                onChange={() => handleSelectEligibleUser(user.id)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-3 py-2">{user.email}</td>
                            <td className="px-3 py-2 text-xs text-gray-500">{user.created_at ? new Date(user.created_at).toLocaleString() : '-'}</td>
                            <td className="px-3 py-2 text-xs text-gray-500">{user.last_seen ? new Date(user.last_seen).toLocaleString() : '-'}</td>
                            <td className="px-3 py-2">{user.has_instagram ? '✅' : '❌'}</td>
                            <td className="px-3 py-2">{user.has_drive ? '✅' : '❌'}</td>
                            <td className="px-3 py-2">{user.has_backup ? '✅' : '❌'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {campaignPreview.total_eligible > campaignPreview.users?.length && (
                    <p className="text-sm text-gray-500 mt-2">
                      Showing {campaignPreview.users?.length} of {campaignPreview.total_eligible} eligible users
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Campaign Stats */}
            {campaignStatus && campaignStatus.total_sent > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard
                  icon={Send}
                  title="Emails Sent"
                  value={campaignStatus.total_sent}
                  color={colors.primary}
                />
                <StatCard
                  icon={MousePointer}
                  title="Clicked"
                  value={campaignStatus.stats?.clicked || 0}
                  subtitle={`${campaignStatus.stats?.click_rate?.toFixed(1) || 0}%`}
                  color={colors.info}
                />
                <StatCard
                  icon={Play}
                  title="Trials Started"
                  value={campaignStatus.stats?.trials_started || 0}
                  subtitle={`${campaignStatus.stats?.trial_rate?.toFixed(1) || 0}%`}
                  color={colors.warning}
                />
                <StatCard
                  icon={CheckCircle}
                  title="Setup Complete"
                  value={campaignStatus.stats?.setup_complete || 0}
                  subtitle={`${campaignStatus.stats?.setup_rate?.toFixed(1) || 0}%`}
                  color={colors.success}
                />
                <StatCard
                  icon={DollarSign}
                  title="Converted"
                  value={campaignStatus.stats?.converted || 0}
                  subtitle={`${campaignStatus.stats?.conversion_rate?.toFixed(1) || 0}%`}
                  color={colors.secondary}
                />
              </div>
            )}

            {/* Campaign Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="font-semibold text-gray-900">Campaign Users ({campaignUsersTotal})</h3>
                <select
                  value={campaignStatusFilter}
                  onChange={(e) => { setCampaignStatusFilter(e.target.value); setCampaignUsersPage(1); }}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Statuses</option>
                  <option value="sent">Sent</option>
                  <option value="clicked">Clicked</option>
                  <option value="trial_started">Trial Started</option>
                  <option value="converted">Converted</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicked</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IG</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drive</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Backup</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {campaignUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            user.status === 'converted' ? 'bg-green-100 text-green-800' :
                            user.status === 'trial_started' ? 'bg-yellow-100 text-yellow-800' :
                            user.status === 'clicked' ? 'bg-blue-100 text-blue-800' :
                            user.status === 'expired' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.email_sent_at ? new Date(user.email_sent_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.email_opened_at ? new Date(user.email_opened_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.clicked_at ? new Date(user.clicked_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.trial_started_at ? new Date(user.trial_started_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.ig_connected_at ? '✅' : '❌'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.drive_connected_at ? '✅' : '❌'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.first_backup_at ? '✅' : '❌'}
                        </td>
                        <td className="px-4 py-3">
                          {!user.trial_started_at && (
                            <button
                              onClick={() => handleResendEmail(user.user_id)}
                              className="text-xs text-indigo-600 hover:text-indigo-800"
                            >
                              Resend
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {campaignUsersTotal > 25 && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Page {campaignUsersPage} of {Math.ceil(campaignUsersTotal / 25)}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCampaignUsersPage(p => Math.max(1, p - 1))}
                      disabled={campaignUsersPage === 1}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCampaignUsersPage(p => p + 1)}
                      disabled={campaignUsersPage >= Math.ceil(campaignUsersTotal / 25)}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <div className="space-y-6">
            {/* Email Stats Cards */}
            {emailStats && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <StatCard
                    icon={Send}
                    title="Emails Sent"
                    value={emailStats.totals?.sent || 0}
                    subtitle={`Last ${emailStats.period_days} days`}
                    color={colors.primary}
                  />
                  <StatCard
                    icon={CheckCircle}
                    title="Delivered"
                    value={emailStats.totals?.delivered || 0}
                    subtitle={`${emailStats.rates?.delivery_rate || 0}% rate`}
                    color={colors.success}
                  />
                  <StatCard
                    icon={Eye}
                    title="Opened"
                    value={emailStats.totals?.opened || 0}
                    subtitle={`${emailStats.rates?.open_rate || 0}% rate`}
                    color={colors.info}
                  />
                  <StatCard
                    icon={MousePointer}
                    title="Clicked"
                    value={emailStats.totals?.clicked || 0}
                    subtitle={`${emailStats.rates?.click_rate || 0}% rate`}
                    color={colors.secondary}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <StatCard
                    icon={AlertCircle}
                    title="Bounced"
                    value={emailStats.totals?.bounced || 0}
                    subtitle={`${emailStats.rates?.bounce_rate || 0}% rate`}
                    color={colors.warning}
                  />
                  <StatCard
                    icon={UserX}
                    title="Complained"
                    value={emailStats.totals?.complained || 0}
                    subtitle="Marked as spam"
                    color={colors.danger}
                  />
                  <StatCard
                    icon={X}
                    title="Failed"
                    value={emailStats.totals?.failed || 0}
                    subtitle="Send failures"
                    color={colors.danger}
                  />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Daily Trend Chart */}
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Trend (Last 7 Days)</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={emailStats.daily_trend || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { weekday: 'short' })} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="sent" stroke={colors.primary} name="Sent" />
                          <Line type="monotone" dataKey="opened" stroke={colors.success} name="Opened" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Email Types Pie Chart */}
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Emails by Type</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={emailStats.by_type || []}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            dataKey="count"
                            nameKey="type"
                            label={({ type, count }) => `${type}: ${count}`}
                          >
                            {(emailStats.by_type || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={Object.values(colors)[index % Object.values(colors).length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Performance by Type Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Performance by Email Type</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivered</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicked</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Rate</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(emailStats.by_type || []).map((type) => (
                          <tr key={type.type}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {type.type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.count}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.delivered}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.opened}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.clicked}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`text-sm font-medium ${type.open_rate >= 20 ? 'text-green-600' : type.open_rate >= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                                {type.open_rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Email Filters and List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Emails ({emailsTotal})</h3>
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={emailFilters.email_type}
                      onChange={(e) => setEmailFilters(prev => ({ ...prev, email_type: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Types</option>
                      <option value="welcome">Welcome</option>
                      <option value="password_reset">Password Reset</option>
                      <option value="limit_reached">Limit Reached</option>
                      <option value="new_posts_detected">New Posts</option>
                      <option value="incomplete_setup">Incomplete Setup</option>
                      <option value="instagram_token_expiring">Token Expiring</option>
                      <option value="instagram_token_expired">Token Expired</option>
                      <option value="affiliate_welcome">Affiliate Welcome</option>
                    </select>
                    <select
                      value={emailFilters.status}
                      onChange={(e) => setEmailFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Status</option>
                      <option value="sent">Sent</option>
                      <option value="delivered">Delivered</option>
                      <option value="bounced">Bounced</option>
                      <option value="complained">Complained</option>
                      <option value="failed">Failed</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Search email..."
                      value={emailFilters.search}
                      onChange={(e) => setEmailFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicked</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allEmails.map((email) => (
                      <tr key={email.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{email.recipient_email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {email.email_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">{email.subject}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            email.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            email.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            email.status === 'bounced' ? 'bg-yellow-100 text-yellow-800' :
                            email.status === 'complained' ? 'bg-red-100 text-red-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {email.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {email.opened ? (
                            <span className="text-green-600 flex items-center">
                              <CheckCircle size={14} className="mr-1" /> {email.open_count}x
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {email.clicked ? (
                            <span className="text-green-600 flex items-center">
                              <CheckCircle size={14} className="mr-1" /> {email.click_count}x
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {email.sent_at ? new Date(email.sent_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                    {allEmails.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          No emails found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Email Automation Tab */}
        {activeTab === 'email-automation' && (
          <div className="space-y-6">
            {/* Sub-tab navigation */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex space-x-2">
                {['flows', 'templates', 'users', 'actions'].map((subTab) => (
                  <button
                    key={subTab}
                    onClick={() => setAutomationSubTab(subTab)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors capitalize ${
                      automationSubTab === subTab
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {subTab}
                  </button>
                ))}
              </div>
            </div>

            {/* Flows Sub-section */}
            {automationSubTab === 'flows' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Email Flows ({emailFlows.length})</h3>
                  <button
                    onClick={fetchEmailFlows}
                    className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-1"
                  >
                    <RefreshCw size={14} />
                    <span>Refresh</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {emailFlows.map((flow) => (
                    <div key={flow.id} className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{flow.name}</h4>
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
                            {flow.trigger_type}
                          </span>
                        </div>
                        <button
                          onClick={() => toggleFlowActive(flow.id, flow.is_active)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            flow.is_active ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              flow.is_active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      {flow.description && (
                        <p className="text-sm text-gray-500 mb-3">{flow.description}</p>
                      )}
                      <div className="flex items-center justify-between text-sm text-gray-600 border-t pt-3">
                        <span>{flow.template_count || 0} templates</span>
                        <span>{flow.stats?.active_users || 0} active</span>
                        <span>{flow.stats?.total_emails_sent || 0} sent</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Templates Sub-section */}
            {automationSubTab === 'templates' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Email Templates ({emailTemplates.length})</h3>
                  <div className="flex items-center space-x-3">
                    <select
                      value={templateFlowFilter}
                      onChange={(e) => setTemplateFlowFilter(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">All Flows</option>
                      {emailFlows.map((flow) => (
                        <option key={flow.id} value={flow.id}>{flow.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={fetchEmailTemplates}
                      className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-1"
                    >
                      <RefreshCw size={14} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flow</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timing</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {emailTemplates.map((template) => (
                        <tr key={template.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => fetchTemplateDetails(template.id)}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{template.flow_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#{template.email_number}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{template.subject}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.timing_display}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {template.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={(e) => { e.stopPropagation(); fetchTemplateDetails(template.id); }}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                      {emailTemplates.length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            No templates found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Users Sub-section */}
            {automationSubTab === 'users' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Users in Flows ({automationUsersTotal})</h3>
                  <div className="flex items-center space-x-3">
                    <select
                      value={automationUserFilter.flow_id}
                      onChange={(e) => setAutomationUserFilter(prev => ({ ...prev, flow_id: e.target.value }))}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Flows</option>
                      {emailFlows.map((flow) => (
                        <option key={flow.id} value={flow.id}>{flow.name}</option>
                      ))}
                    </select>
                    <select
                      value={automationUserFilter.status}
                      onChange={(e) => setAutomationUserFilter(prev => ({ ...prev, status: e.target.value }))}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="stopped">Stopped</option>
                    </select>
                    <button
                      onClick={fetchAutomationUsers}
                      className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-1"
                    >
                      <RefreshCw size={14} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flow</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {automationUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{user.user_email}</div>
                            <div className="text-xs text-gray-500">{user.user_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.flow_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            Email {user.current_email_number} ({user.emails_sent} sent)
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              user.status === 'active' ? 'bg-green-100 text-green-800' :
                              user.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {user.status}
                            </span>
                            {user.stopped_reason && (
                              <span className="ml-1 text-xs text-gray-500">({user.stopped_reason})</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.next_email_due_at ? new Date(user.next_email_due_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {user.status === 'active' && (
                              <button
                                onClick={() => stopUserFromFlow(user.user_id, user.flow_id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Stop
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {automationUsers.length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            No users in flows
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                {automationUsersTotal > 0 && (
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                    <div className="text-sm text-gray-700">
                      Showing {automationUsersPage * automationUsersLimit + 1} to {Math.min((automationUsersPage + 1) * automationUsersLimit, automationUsersTotal)} of {automationUsersTotal} users
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setAutomationUsersPage(0)}
                        disabled={automationUsersPage === 0}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setAutomationUsersPage(prev => Math.max(0, prev - 1))}
                        disabled={automationUsersPage === 0}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-sm text-gray-700">
                        Page {automationUsersPage + 1} of {Math.ceil(automationUsersTotal / automationUsersLimit)}
                      </span>
                      <button
                        onClick={() => setAutomationUsersPage(prev => prev + 1)}
                        disabled={(automationUsersPage + 1) * automationUsersLimit >= automationUsersTotal}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setAutomationUsersPage(Math.ceil(automationUsersTotal / automationUsersLimit) - 1)}
                        disabled={(automationUsersPage + 1) * automationUsersLimit >= automationUsersTotal}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions Sub-section */}
            {automationSubTab === 'actions' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-lg bg-indigo-100">
                        <RefreshCw size={24} className="text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <h4 className="font-semibold text-gray-900">Check Triggers</h4>
                        <p className="text-sm text-gray-500">Evaluate user states and enroll eligible users in flows</p>
                      </div>
                    </div>
                    <button
                      onClick={triggerEmailCheck}
                      disabled={triggerCheckLoading}
                      className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {triggerCheckLoading ? (
                        <>
                          <RefreshCw size={16} className="animate-spin mr-2" />
                          Running...
                        </>
                      ) : (
                        'Run Trigger Check Now'
                      )}
                    </button>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-lg bg-green-100">
                        <Send size={24} className="text-green-600" />
                      </div>
                      <div className="ml-4">
                        <h4 className="font-semibold text-gray-900">Send Due Emails</h4>
                        <p className="text-sm text-gray-500">Send all emails that are currently due</p>
                      </div>
                    </div>
                    <button
                      onClick={triggerEmailSend}
                      disabled={triggerSendLoading}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {triggerSendLoading ? (
                        <>
                          <RefreshCw size={16} className="animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        'Send Due Emails Now'
                      )}
                    </button>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-4">Automation Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-600">{emailFlows.length}</p>
                      <p className="text-sm text-gray-500">Total Flows</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{emailFlows.filter(f => f.is_active).length}</p>
                      <p className="text-sm text-gray-500">Active Flows</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{emailTemplates.length}</p>
                      <p className="text-sm text-gray-500">Total Templates</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {automationUsers.filter(u => u.status === 'active').length}
                      </p>
                      <p className="text-sm text-gray-500">Active Users</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Template Edit Modal */}
        {showTemplateModal && selectedTemplate && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowTemplateModal(false)} />
              <div className="relative inline-block w-full max-w-5xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Edit Email Template</h3>
                    <p className="text-sm text-gray-500">{selectedTemplate.flow_name} - Email #{selectedTemplate.email_number}</p>
                  </div>
                  <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Edit Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                      <input
                        type="text"
                        value={selectedTemplate.subject || ''}
                        onChange={(e) => setSelectedTemplate(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject Alt (A/B)</label>
                      <input
                        type="text"
                        value={selectedTemplate.subject_alt || ''}
                        onChange={(e) => setSelectedTemplate(prev => ({ ...prev, subject_alt: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Timing Value</label>
                        <input
                          type="number"
                          value={selectedTemplate.timing_value || 0}
                          onChange={(e) => setSelectedTemplate(prev => ({ ...prev, timing_value: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Timing Unit</label>
                        <select
                          value={selectedTemplate.timing_unit || 'days'}
                          onChange={(e) => setSelectedTemplate(prev => ({ ...prev, timing_unit: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
                      <input
                        type="text"
                        value={selectedTemplate.cta_text || ''}
                        onChange={(e) => setSelectedTemplate(prev => ({ ...prev, cta_text: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA URL</label>
                      <input
                        type="text"
                        value={selectedTemplate.cta_url || ''}
                        onChange={(e) => setSelectedTemplate(prev => ({ ...prev, cta_url: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">HTML Content</label>
                        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => setTemplateEditorMode('visual')}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                              templateEditorMode === 'visual' 
                                ? 'bg-white text-indigo-600 shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <Eye size={14} className="inline mr-1" />
                            Visual
                          </button>
                          <button
                            type="button"
                            onClick={() => setTemplateEditorMode('code')}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                              templateEditorMode === 'code' 
                                ? 'bg-white text-indigo-600 shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <Code size={14} className="inline mr-1" />
                            Code
                          </button>
                        </div>
                      </div>
                      {templateEditorMode === 'visual' ? (
                        <div className="border border-gray-300 rounded-lg overflow-hidden email-editor">
                          <style>{emailEditorStyles}</style>
                          <ReactQuill
                            theme="snow"
                            value={selectedTemplate.html_content || ''}
                            onChange={(content) => setSelectedTemplate(prev => ({ ...prev, html_content: content }))}
                            modules={quillModules}
                            style={{ minHeight: '300px' }}
                          />
                        </div>
                      ) : (
                        <textarea
                          value={selectedTemplate.html_content || ''}
                          onChange={(e) => setSelectedTemplate(prev => ({ ...prev, html_content: e.target.value }))}
                          rows={12}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                          placeholder="Enter raw HTML code..."
                        />
                      )}
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={selectedTemplate.is_active}
                        onChange={(e) => setSelectedTemplate(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">Active</label>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50 h-96">
                      <iframe
                        srcDoc={selectedTemplate.preview_html || selectedTemplate.html_content}
                        title="Email Preview"
                        className="w-full h-full"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-6 pt-6 border-t">
                  <button
                    onClick={() => sendTemplatePreview(selectedTemplate.id)}
                    disabled={templatePreviewSending}
                    className="px-4 py-2 text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50"
                  >
                    {templatePreviewSending ? 'Sending...' : 'Send Test Email'}
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowTemplateModal(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => updateEmailTemplate(selectedTemplate.id, {
                        subject: selectedTemplate.subject,
                        subject_alt: selectedTemplate.subject_alt,
                        timing_value: selectedTemplate.timing_value,
                        timing_unit: selectedTemplate.timing_unit,
                        cta_text: selectedTemplate.cta_text,
                        cta_url: selectedTemplate.cta_url,
                        html_content: selectedTemplate.html_content,
                        is_active: selectedTemplate.is_active
                      })}
                      disabled={templateSaving}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                      {templateSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <div className="space-y-6">
            {/* Affiliate Dashboard Stats */}
            {affiliateDashboard?.summary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                  icon={Briefcase}
                  title="Total Affiliates"
                  value={affiliateDashboard.summary.total_affiliates || 0}
                  subtitle="Active partners"
                  color={colors.primary}
                />
                <StatCard
                  icon={MousePointer}
                  title="Total Clicks"
                  value={affiliateDashboard.summary.total_visits || 0}
                  subtitle="Affiliate link clicks"
                  color={colors.info}
                />
                <StatCard
                  icon={UserPlus}
                  title="Conversions"
                  value={affiliateDashboard.summary.total_conversions || 0}
                  subtitle={`${affiliateDashboard.summary.total_signups || 0} signups, ${affiliateDashboard.summary.total_upgrades || 0} upgrades`}
                  color={colors.success}
                />
                <StatCard
                  icon={DollarSign}
                  title="Pending Payouts"
                  value={`$${(affiliateDashboard.summary.pending_commission || 0).toFixed(2)}`}
                  subtitle={`$${(affiliateDashboard.summary.total_paid || 0).toFixed(2)} paid`}
                  color={colors.warning}
                />
              </div>
            )}

            {/* Pending Applications Section */}
            {affiliateApplications.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-amber-200 mb-6">
                <div className="p-6 border-b border-amber-200 bg-amber-50 rounded-t-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Clock size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Pending Applications</h3>
                        <p className="text-sm text-gray-600">{affiliateApplications.length} application(s) awaiting review</p>
                      </div>
                    </div>
                    <button
                      onClick={fetchAffiliateApplications}
                      className="px-4 py-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <RefreshCw size={16} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applicant</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {affiliateApplications.map((app) => (
                        <tr key={app.user_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{app.full_name || 'No name'}</div>
                              <div className="text-sm text-gray-500">{app.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {app.application_date ? new Date(app.application_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              app.subscription_tier === 'pro' ? 'bg-purple-100 text-purple-800' :
                              app.subscription_tier === 'premium' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {app.subscription_tier || 'free'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                            {app.notes || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedApplication(app);
                                  setApprovalSettings({ signup_commission: '1.00', upgrade_commission: '5.00', notes: '' });
                                  setShowApproveModal(true);
                                }}
                                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedApplication(app);
                                  setRejectionReason('');
                                  setShowRejectModal(true);
                                }}
                                className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Affiliates Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Affiliates</h3>
                  <button
                    onClick={() => {
                      fetchAffiliateDashboard();
                      fetchAffiliates();
                      fetchAffiliateApplications();
                    }}
                    className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <RefreshCw size={16} />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affiliate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signups</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Upgrades</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission Rates</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {affiliates.map((affiliate) => (
                      <tr key={affiliate.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{affiliate.full_name || 'No name'}</div>
                            <div className="text-sm text-gray-500">{affiliate.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded">{affiliate.affiliate_code}</code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(affiliate.affiliate_link);
                                toast.success('Link copied!');
                              }}
                              className="text-gray-400 hover:text-indigo-600"
                              title="Copy affiliate link"
                            >
                              <ExternalLink size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {affiliate.total_visits || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {affiliate.total_signups || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {affiliate.total_upgrades || 0}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <div className="flex flex-col space-y-2">
                            {/* Signup Commission */}
                            <div className="flex items-start">
                              <span className="text-gray-500 w-16 shrink-0">Signup:</span>
                              {affiliate.signup_payout_type === 'revshare' ? (
                                <span className="text-purple-600 font-medium">{affiliate.signup_revshare_percent || 0}%</span>
                              ) : (
                                <span className="font-medium">${affiliate.signup_commission?.toFixed(2) || '0.00'}</span>
                              )}
                            </div>
                            {/* Upgrade Commission */}
                            <div className="flex items-start">
                              <span className="text-gray-500 w-16 shrink-0">Upgrade:</span>
                              {affiliate.upgrade_payout_type === 'revshare' ? (
                                <div className="flex flex-col leading-tight">
                                  <span className="text-blue-600 font-medium">
                                    {affiliate.monthly_revshare_percent || affiliate.upgrade_revshare_percent || 0}% mo
                                    {affiliate.revshare_months ? ` ×${affiliate.revshare_months}` : ''}
                                  </span>
                                  <span className="text-green-600 font-medium">
                                    {affiliate.annual_revshare_percent || affiliate.upgrade_revshare_percent || 0}% yr
                                  </span>
                                </div>
                              ) : (
                                <span className="font-medium">${affiliate.upgrade_commission?.toFixed(2) || '0.00'}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${affiliate.pending_commission > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
                            ${affiliate.pending_commission?.toFixed(2) || '0.00'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewAffiliate(affiliate)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAffiliate(affiliate);
                                setNewPayout({ amount: '', payment_method: '', transaction_reference: '', notes: '' });
                                setShowPayoutModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="Create Payout"
                            >
                              <CreditCard size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {affiliates.length === 0 && (
                      <tr>
                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                          No affiliates found. Use the Users tab to make a user an affiliate.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Affiliate Detail Modal */}
            {showAffiliateModal && selectedAffiliate && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedAffiliate.full_name || selectedAffiliate.email}</h3>
                      <p className="text-sm text-gray-500">{selectedAffiliate.email}</p>
                    </div>
                    <button onClick={() => setShowAffiliateModal(false)} className="text-gray-400 hover:text-gray-600">
                      <X size={24} />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    {/* Affiliate Link with Sub-ID placeholders */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Affiliate Link (with tracking placeholders)</label>
                      <p className="text-xs text-gray-500 mb-2">Share this link with the affiliate. They can replace {'{SUB1}'}, {'{SUB2}'}, and {'{SUB3}'} with their own tracking values.</p>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          readOnly
                          value={selectedAffiliate.affiliate_link_with_subs || selectedAffiliate.affiliate_link}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-mono"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedAffiliate.affiliate_link_with_subs || selectedAffiliate.affiliate_link);
                            toast.success('Link copied!');
                          }}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                    
                    {/* Base Affiliate Link */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Base Link (no tracking params)</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          readOnly
                          value={selectedAffiliate.affiliate_link}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-mono"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedAffiliate.affiliate_link);
                            toast.success('Link copied!');
                          }}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    {/* Commission Settings */}
                    <div className="space-y-4">
                      {/* Signup Commission */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-3">Signup Commission</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type</label>
                            <select
                              value={affiliateSettings.signup_payout_type}
                              onChange={(e) => setAffiliateSettings({ ...affiliateSettings, signup_payout_type: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="fixed">Fixed Amount</option>
                              <option value="revshare">Revenue Share %</option>
                            </select>
                          </div>
                          {affiliateSettings.signup_payout_type === 'fixed' ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Amount ($)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={affiliateSettings.signup_commission}
                                onChange={(e) => setAffiliateSettings({ ...affiliateSettings, signup_commission: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="0.00"
                              />
                            </div>
                          ) : (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Revshare Percent (%)</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                max="100"
                                value={affiliateSettings.signup_revshare_percent}
                                onChange={(e) => setAffiliateSettings({ ...affiliateSettings, signup_revshare_percent: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="20"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Upgrade Commission */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-3">Upgrade Commission</h4>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type</label>
                            <select
                              value={affiliateSettings.upgrade_payout_type}
                              onChange={(e) => setAffiliateSettings({ ...affiliateSettings, upgrade_payout_type: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="fixed">Fixed Amount</option>
                              <option value="revshare">Revenue Share %</option>
                            </select>
                          </div>
                          {affiliateSettings.upgrade_payout_type === 'fixed' ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Amount ($)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={affiliateSettings.upgrade_commission}
                                onChange={(e) => setAffiliateSettings({ ...affiliateSettings, upgrade_commission: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="0.00"
                              />
                            </div>
                          ) : (
                            <div className="col-span-2">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Revshare (%)</label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={affiliateSettings.monthly_revshare_percent}
                                    onChange={(e) => setAffiliateSettings({ ...affiliateSettings, monthly_revshare_percent: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    placeholder="30"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Recurring commission per month</p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revshare (%)</label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={affiliateSettings.annual_revshare_percent}
                                    onChange={(e) => setAffiliateSettings({ ...affiliateSettings, annual_revshare_percent: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    placeholder="20"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">One-time commission</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {affiliateSettings.upgrade_payout_type === 'revshare' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Revshare Duration (months)</label>
                            <input
                              type="number"
                              min="1"
                              max="36"
                              value={affiliateSettings.revshare_months}
                              onChange={(e) => setAffiliateSettings({ ...affiliateSettings, revshare_months: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              placeholder="12"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Number of months to pay recurring commission for monthly subscriptions.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleUpdateAffiliateSettings}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Save Commission Settings
                    </button>

                    {/* Payout Framework Summary - Display current configuration */}
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
                      <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                        <span className="mr-2">💰</span> Payout Framework
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-gray-500 mb-1">Signup Commission</div>
                          <div className="font-semibold text-gray-800">
                            {selectedAffiliate.signup_payout_type === 'revshare' ? (
                              <span>{selectedAffiliate.signup_revshare_percent || 0}% of subscription</span>
                            ) : (
                              <span>${selectedAffiliate.signup_commission?.toFixed(2) || '0.00'} fixed</span>
                            )}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-gray-500 mb-1">Upgrade Commission</div>
                          <div className="font-semibold text-gray-800">
                            {selectedAffiliate.upgrade_payout_type === 'revshare' ? (
                              <>
                                <div className="text-sm">
                                  <span className="text-blue-600">{selectedAffiliate.monthly_revshare_percent || selectedAffiliate.upgrade_revshare_percent || 0}%</span> monthly
                                  {selectedAffiliate.revshare_months && (
                                    <span className="text-gray-500"> for {selectedAffiliate.revshare_months} months</span>
                                  )}
                                </div>
                                <div className="text-sm">
                                  <span className="text-green-600">{selectedAffiliate.annual_revshare_percent || selectedAffiliate.upgrade_revshare_percent || 0}%</span> annual (one-time)
                                </div>
                              </>
                            ) : (
                              <span>${selectedAffiliate.upgrade_commission?.toFixed(2) || '0.00'} fixed</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Projected Earnings */}
                      {(selectedAffiliate.projected_earnings > 0 || selectedAffiliate.active_revshare_users > 0) && (
                        <div className="mt-3 pt-3 border-t border-indigo-100">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-gray-600">Projected Revshare Earnings:</span>
                              <span className="text-xs text-gray-500 ml-1">
                                ({selectedAffiliate.active_revshare_users || 0} active users)
                              </span>
                            </div>
                            <span className="font-bold text-green-600">
                              ${selectedAffiliate.projected_earnings?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Based on active subscribers continuing to pay for remaining months.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">{selectedAffiliate.total_visits || 0}</div>
                        <div className="text-sm text-gray-600">Clicks</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{selectedAffiliate.total_signups || 0}</div>
                        <div className="text-sm text-gray-600">Signups</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-purple-600">{selectedAffiliate.total_upgrades || 0}</div>
                        <div className="text-sm text-gray-600">Upgrades</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-amber-600">${selectedAffiliate.pending_commission?.toFixed(2) || '0.00'}</div>
                        <div className="text-sm text-gray-600">Pending</div>
                      </div>
                    </div>

                    {/* Conversions */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Conversions</h4>
                      <div className="bg-gray-50 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Commission</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {affiliateConversions.slice(0, 10).map((conv) => (
                              <tr key={conv.id}>
                                <td className="px-4 py-2 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${conv.conversion_type === 'signup' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                                    {conv.conversion_type}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">{conv.converted_user?.email || 'Unknown'}</td>
                                <td className="px-4 py-2 text-sm font-medium">${conv.commission_amount?.toFixed(2)}</td>
                                <td className="px-4 py-2 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${conv.is_paid ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                    {conv.is_paid ? 'Paid' : 'Pending'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500">{new Date(conv.created_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                            {affiliateConversions.length === 0 && (
                              <tr>
                                <td colSpan="5" className="px-4 py-4 text-center text-gray-500">No conversions yet</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Payouts */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Payout History</h4>
                        <button
                          onClick={() => {
                            setNewPayout({ amount: '', payment_method: '', transaction_reference: '', notes: '' });
                            setShowPayoutModal(true);
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          + New Payout
                        </button>
                      </div>
                      <div className="bg-gray-50 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Amount</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Method</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Reference</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {affiliatePayouts.map((payout) => (
                              <tr key={payout.id}>
                                <td className="px-4 py-2 text-sm font-medium text-green-600">${payout.amount?.toFixed(2)}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{payout.payment_method || '-'}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{payout.transaction_reference || '-'}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{new Date(payout.payout_date).toLocaleDateString()}</td>
                              </tr>
                            ))}
                            {affiliatePayouts.length === 0 && (
                              <tr>
                                <td colSpan="4" className="px-4 py-4 text-center text-gray-500">No payouts yet</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payout Modal */}
            {showPayoutModal && selectedAffiliate && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Create Payout for {selectedAffiliate.full_name || selectedAffiliate.email}</h3>
                    <button onClick={() => setShowPayoutModal(false)} className="text-gray-400 hover:text-gray-600">
                      <X size={24} />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-amber-800">
                        Pending commission: <strong>${selectedAffiliate.pending_commission?.toFixed(2) || '0.00'}</strong>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newPayout.amount}
                        onChange={(e) => setNewPayout({ ...newPayout, amount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                      <select
                        value={newPayout.payment_method}
                        onChange={(e) => setNewPayout({ ...newPayout, payment_method: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select method...</option>
                        <option value="paypal">PayPal</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="crypto">Crypto</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                      <input
                        type="text"
                        value={newPayout.transaction_reference}
                        onChange={(e) => setNewPayout({ ...newPayout, transaction_reference: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., PayPal TX ID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={newPayout.notes}
                        onChange={(e) => setNewPayout({ ...newPayout, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows="2"
                        placeholder="Optional notes..."
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        onClick={() => setShowPayoutModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreatePayout}
                        disabled={!newPayout.amount}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Create Payout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Approve Application Modal */}
            {showApproveModal && selectedApplication && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 my-8">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Approve Application</h3>
                    <button onClick={() => setShowApproveModal(false)} className="text-gray-400 hover:text-gray-600">
                      <X size={24} />
                    </button>
                  </div>
                  <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-green-800">
                        Approving affiliate application for <strong>{selectedApplication.full_name || selectedApplication.email}</strong>
                      </p>
                    </div>

                    {/* Commission Settings */}
                    <div className="space-y-4">
                      {/* Signup Commission */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-3">Signup Commission</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type</label>
                            <select
                              value={approvalSettings.signup_payout_type}
                              onChange={(e) => setApprovalSettings({ ...approvalSettings, signup_payout_type: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="fixed">Fixed Amount</option>
                              <option value="revshare">Revenue Share %</option>
                            </select>
                          </div>
                          {approvalSettings.signup_payout_type === 'fixed' ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Amount ($)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={approvalSettings.signup_commission}
                                onChange={(e) => setApprovalSettings({ ...approvalSettings, signup_commission: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="1.00"
                              />
                            </div>
                          ) : (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Revshare Percent (%)</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                max="100"
                                value={approvalSettings.signup_revshare_percent}
                                onChange={(e) => setApprovalSettings({ ...approvalSettings, signup_revshare_percent: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="20"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Upgrade Commission */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-3">Upgrade Commission</h4>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type</label>
                            <select
                              value={approvalSettings.upgrade_payout_type}
                              onChange={(e) => setApprovalSettings({ ...approvalSettings, upgrade_payout_type: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="fixed">Fixed Amount</option>
                              <option value="revshare">Revenue Share %</option>
                            </select>
                          </div>
                          {approvalSettings.upgrade_payout_type === 'fixed' ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Amount ($)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={approvalSettings.upgrade_commission}
                                onChange={(e) => setApprovalSettings({ ...approvalSettings, upgrade_commission: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="5.00"
                              />
                            </div>
                          ) : (
                            <div className="col-span-2">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Revshare (%)</label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={approvalSettings.monthly_revshare_percent}
                                    onChange={(e) => setApprovalSettings({ ...approvalSettings, monthly_revshare_percent: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    placeholder="30"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Recurring commission per month</p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revshare (%)</label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={approvalSettings.annual_revshare_percent}
                                    onChange={(e) => setApprovalSettings({ ...approvalSettings, annual_revshare_percent: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    placeholder="20"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">One-time commission</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {approvalSettings.upgrade_payout_type === 'revshare' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Revshare Duration (months)</label>
                            <input
                              type="number"
                              min="1"
                              max="36"
                              value={approvalSettings.revshare_months}
                              onChange={(e) => setApprovalSettings({ ...approvalSettings, revshare_months: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              placeholder="12"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Number of months to pay recurring commission for monthly subscriptions.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes (optional)</label>
                      <textarea
                        value={approvalSettings.notes}
                        onChange={(e) => setApprovalSettings({ ...approvalSettings, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows="2"
                        placeholder="Internal notes..."
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        onClick={() => setShowApproveModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleApproveApplication}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Approve Application
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Reject Application Modal */}
            {showRejectModal && selectedApplication && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Reject Application</h3>
                    <button onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-gray-600">
                      <X size={24} />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-800">
                        Rejecting affiliate application for <strong>{selectedApplication.full_name || selectedApplication.email}</strong>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason (optional)</label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows="3"
                        placeholder="Reason for rejection..."
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        onClick={() => setShowRejectModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRejectApplication}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Reject Application
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* OAuth Logs Tab */}
        {activeTab === 'oauth' && (
          <div className="space-y-6">
            {/* OAuth Stats Summary */}
            {oauthLogsStats && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <StatCard
                  icon={Link2}
                  title="Total Attempts"
                  value={oauthLogsStats.total_attempts || 0}
                  subtitle="Started OAuth flow"
                  color={colors.info}
                />
                <StatCard
                  icon={RefreshCw}
                  title="Callbacks"
                  value={oauthLogsStats.total_callbacks || 0}
                  subtitle={`${oauthLogsStats.total_attempts > 0 ? Math.round((oauthLogsStats.total_callbacks / oauthLogsStats.total_attempts) * 100) : 0}% return rate`}
                  color={colors.secondary}
                />
                <StatCard
                  icon={UserX}
                  title="Abandoned"
                  value={oauthLogsStats.total_abandoned || 0}
                  subtitle="Never returned from IG"
                  color={colors.warning}
                />
                <StatCard
                  icon={CheckCircle}
                  title="Successful"
                  value={oauthLogsStats.total_success || 0}
                  subtitle={`${oauthLogsStats.by_provider?.instagram?.success_rate || 0}% IG success rate`}
                  color={colors.success}
                />
                <StatCard
                  icon={AlertCircle}
                  title="Errors"
                  value={oauthLogsStats.total_errors || 0}
                  subtitle="Connection failures"
                  color={colors.danger}
                />
              </div>
            )}
            
            {/* Instagram Funnel Analysis */}
            {oauthLogsStats?.by_provider?.instagram && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Instagram Connection Funnel</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-700">{oauthLogsStats.by_provider.instagram.attempts || 0}</div>
                    <div className="text-sm text-blue-600">Started</div>
                  </div>
                  <div className="text-center p-4 bg-indigo-50 rounded-lg">
                    <div className="text-2xl font-bold text-indigo-700">{oauthLogsStats.by_provider.instagram.callback_received || 0}</div>
                    <div className="text-sm text-indigo-600">Returned from IG</div>
                    <div className="text-xs text-indigo-500 mt-1">{oauthLogsStats.by_provider.instagram.callback_rate || 0}% return rate</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-700">{oauthLogsStats.by_provider.instagram.success || 0}</div>
                    <div className="text-sm text-green-600">Connected</div>
                    <div className="text-xs text-green-500 mt-1">{oauthLogsStats.by_provider.instagram.success_rate || 0}% success rate</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-700">{oauthLogsStats.by_provider.instagram.abandoned || 0}</div>
                    <div className="text-sm text-orange-600">Abandoned</div>
                    <div className="text-xs text-orange-500 mt-1">Never completed</div>
                  </div>
                </div>
              </div>
            )}

            {/* Common Errors */}
            {oauthLogsStats?.common_errors?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Errors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {oauthLogsStats.common_errors.slice(0, 6).map((error, idx) => (
                    <div key={idx} className="p-4 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-red-800">{error.error_type || 'Unknown'}</span>
                        <span className="text-lg font-bold text-red-600">{error.count}</span>
                      </div>
                      <span className="text-xs text-red-600 capitalize">{error.provider}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* OAuth Logs Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Connection Logs</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{oauthLogsTotal} total logs</span>
                    <button
                      onClick={exportOAuthLogsToCSV}
                      className="px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center space-x-2"
                      title="Export filtered logs to CSV"
                    >
                      <Download size={16} />
                      <span>Export CSV</span>
                    </button>
                    <button
                      onClick={fetchOAuthLogs}
                      className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <RefreshCw size={16} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
                
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <select
                    value={oauthLogsFilters.provider}
                    onChange={(e) => handleOauthFilterChange('provider', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Providers</option>
                    <option value="instagram">Instagram</option>
                    <option value="google">Google Drive</option>
                    <option value="facebook">Facebook</option>
                  </select>
                  <select
                    value={oauthLogsFilters.event_type}
                    onChange={(e) => handleOauthFilterChange('event_type', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Events</option>
                    <option value="attempt">Attempts</option>
                    <option value="callback_received">Callbacks</option>
                    <option value="success">Success</option>
                    <option value="warning">Warnings</option>
                    <option value="error">Errors</option>
                  </select>
                  <select
                    value={oauthLogsFilters.error_type}
                    onChange={(e) => handleOauthFilterChange('error_type', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Error Types</option>
                    <option value="access_denied">Access Denied</option>
                    <option value="personal_account">Personal Account</option>
                    <option value="token_error">Token Error</option>
                    <option value="http_error">HTTP Error</option>
                    <option value="exception">Exception</option>
                    <option value="invalid_callback">Invalid Callback</option>
                  </select>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Browser</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OS</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {oauthLogs.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                          No OAuth connection logs found
                        </td>
                      </tr>
                    ) : (
                      oauthLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">{log.user_email || '-'}</div>
                              {log.account_username && (
                                <div className="text-gray-500">@{log.account_username}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.provider === 'instagram' ? 'bg-pink-100 text-pink-800' :
                              log.provider === 'google' ? 'bg-blue-100 text-blue-800' :
                              log.provider === 'facebook' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {log.provider}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.event_type === 'success' ? 'bg-green-100 text-green-800' :
                              log.event_type === 'error' ? 'bg-red-100 text-red-800' :
                              log.event_type === 'callback_received' ? 'bg-blue-100 text-blue-800' :
                              log.event_type === 'warning' ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {log.event_type === 'callback_received' ? 'callback' : log.event_type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {log.actual_account_type || log.selected_account_type ? (
                              <div className="text-xs space-y-1">
                                {log.actual_account_type && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                                    log.actual_account_type === 'BUSINESS' ? 'bg-green-100 text-green-800' :
                                    log.actual_account_type === 'MEDIA_CREATOR' ? 'bg-blue-100 text-blue-800' :
                                    log.actual_account_type === 'PERSONAL' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {log.actual_account_type === 'MEDIA_CREATOR' ? 'Creator' : log.actual_account_type}
                                  </span>
                                )}
                                {log.selected_account_type && !log.actual_account_type && (
                                  <span className="text-gray-500" title="Selected in modal, not confirmed">
                                    ({log.selected_account_type})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {log.device_type ? (
                              <div className="space-y-0.5">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  log.device_type === 'mobile' ? 'bg-purple-100 text-purple-800' :
                                  log.device_type === 'tablet' ? 'bg-blue-100 text-blue-800' :
                                  log.device_type === 'desktop' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {log.device_type}
                                </span>
                                {(log.device_brand || log.device_model) && (
                                  <div className="text-xs text-gray-500">
                                    {log.device_brand}{log.device_model ? ` ${log.device_model}` : ''}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {log.browser ? (
                              <div className="space-y-0.5">
                                <span className="text-gray-900">{log.browser}</span>
                                {log.browser_version && (
                                  <div className="text-xs text-gray-500">v{log.browser_version.split('.')[0]}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {log.operating_system ? (
                              <div className="space-y-0.5">
                                <span className="text-gray-900">{log.operating_system}</span>
                                {log.os_version && (
                                  <div className="text-xs text-gray-500">{log.os_version}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {log.error_type || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {log.error_message ? (
                              <span className="max-w-xs truncate block" title={log.error_message}>
                                {log.error_message.length > 40 ? `${log.error_message.substring(0, 40)}...` : log.error_message}
                              </span>
                            ) : log.metadata ? (
                              <button 
                                className="text-indigo-600 hover:text-indigo-800 text-xs"
                                onClick={() => {
                                  alert(JSON.stringify(log.metadata, null, 2));
                                }}
                              >
                                View metadata
                              </button>
                            ) : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {oauthLogsTotal > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>Show</span>
                    <select
                      value={oauthLogsPerPage}
                      onChange={(e) => {
                        setOauthLogsPerPage(Number(e.target.value));
                        setOauthLogsPage(1);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={250}>250</option>
                      <option value={500}>500</option>
                    </select>
                    <span>per page</span>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    Showing {((oauthLogsPage - 1) * oauthLogsPerPage) + 1} - {Math.min(oauthLogsPage * oauthLogsPerPage, oauthLogsTotal)} of {oauthLogsTotal} logs
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setOauthLogsPage(1)}
                      disabled={oauthLogsPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setOauthLogsPage(p => Math.max(1, p - 1))}
                      disabled={oauthLogsPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-600">
                      Page {oauthLogsPage} of {Math.ceil(oauthLogsTotal / oauthLogsPerPage)}
                    </span>
                    <button
                      onClick={() => setOauthLogsPage(p => Math.min(Math.ceil(oauthLogsTotal / oauthLogsPerPage), p + 1))}
                      disabled={oauthLogsPage >= Math.ceil(oauthLogsTotal / oauthLogsPerPage)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setOauthLogsPage(Math.ceil(oauthLogsTotal / oauthLogsPerPage))}
                      disabled={oauthLogsPage >= Math.ceil(oauthLogsTotal / oauthLogsPerPage)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Service Status */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Status</h3>
              <div className="space-y-3">
                {servicesStatus && Object.entries(servicesStatus).map(([service, info]) => (
                  <ServiceStatusCard
                    key={service}
                    service={service}
                    status={info.status}
                    details={info.details}
                    onAction={(service, action) => {
                      if (window.confirm(`Are you sure you want to ${action} ${service}?`)) {
                        handleServiceAction(service, action);
                      }
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Celery Queues */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Celery Queues</h3>
              {celeryQueues && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{celeryQueues.active || 0}</p>
                      <p className="text-sm text-gray-500">Active</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{celeryQueues.scheduled || 0}</p>
                      <p className="text-sm text-gray-500">Scheduled</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{celeryQueues.reserved || 0}</p>
                      <p className="text-sm text-gray-500">Reserved</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{celeryQueues.failed || 0}</p>
                      <p className="text-sm text-gray-500">Failed</p>
                    </div>
                  </div>
                  {((celeryQueues?.active || 0) + (celeryQueues?.scheduled || 0) + (celeryQueues?.reserved || 0)) > 100 && (
                    <button
                      onClick={handlePurgeQueue}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                    >
                      <Trash2 size={16} />
                      <span>Purge Queue</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Survey Tab */}
        {activeTab === 'survey' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Instagram Survey Responses</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Feedback from users who attempted but didn't complete Instagram connection
                </p>
              </div>

              {/* Summary Stats */}
              {Object.keys(surveySummary).length > 0 && (
                <div className="p-6 bg-gray-50 border-b border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Response Summary</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(surveySummary).map(([reason, count]) => (
                      <span 
                        key={reason}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                      >
                        {reason}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {surveyLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="animate-spin h-8 w-8 text-indigo-600" />
                </div>
              ) : surveyResponses.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reason
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Comment
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {surveyResponses.map((response) => (
                        <tr key={response.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {response.user_name || 'No name'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {response.user_email}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {response.reason}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600 max-w-xs truncate">
                              {response.comment || '—'}
                            </p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {response.created_at ? new Date(response.created_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-gray-500">No survey responses yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Blog SEO Tab - Using new component */}
        {activeTab === 'blog' && (
          <BlogAdminPanel />
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="space-y-6">
            {['backend', 'celery', 'nginx'].map((service) => (
              <div key={service} className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">{service} Logs</h3>
                    <button
                      onClick={() => fetchLogs(service)}
                      className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Download size={16} />
                      <span>Load Logs</span>
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {logs[service] ? (
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                      {logs[service]}
                    </pre>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      Click "Load Logs" to view {service} logs
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* User Emails Modal */}
      {showEmailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Sent Emails</h3>
                {selectedUserForEmails && (
                  <p className="text-sm text-gray-500">{selectedUserForEmails.email}</p>
                )}
              </div>
              <button
                onClick={() => setShowEmailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              {userEmailsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="animate-spin text-indigo-600" size={32} />
                </div>
              ) : userEmails.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Mail size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No emails sent to this user</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicked</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userEmails.map((email) => (
                      <tr key={email.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {email.email_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={email.subject}>
                          {email.subject}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            email.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            email.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            email.status === 'bounced' ? 'bg-yellow-100 text-yellow-800' :
                            email.status === 'complained' ? 'bg-red-100 text-red-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {email.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {email.sent_at ? new Date(email.sent_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {email.opened ? (
                            <div className="text-green-600">
                              <div className="flex items-center">
                                <CheckCircle size={14} className="mr-1" />
                                {email.open_count}x
                              </div>
                              {email.first_opened_at && (
                                <div className="text-xs text-gray-400">
                                  First: {new Date(email.first_opened_at).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">Not opened</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {email.clicked ? (
                            <div className="text-green-600">
                              <div className="flex items-center">
                                <CheckCircle size={14} className="mr-1" />
                                {email.click_count}x
                              </div>
                              {email.first_clicked_at && (
                                <div className="text-xs text-gray-400">
                                  First: {new Date(email.first_clicked_at).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">No clicks</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowEmailsModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to format bytes
function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}